// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import {
  Animated,
  FlatList,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  Easing,
  Platform,
  ImageBackground,
  Image,
  useWindowDimensions,
  PanResponder,
  View as NativeView,
} from 'react-native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useGlobalSearchParams, useLocalSearchParams, useRouter, useRootNavigationState, usePathname } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';

import { Text, View } from '@/components/Themed';
import { t } from '@/i18n';
import {
  BffWebSocketClient,
  getSharedBffWebSocket,
  GameResultsEvent,
  HoleUpdateEvent,
  HoleInfoEvent,
  RoundStateEvent,
} from '@/services/bffWebSocket';
import { createAppLogger } from '@/services/appLogger';
import { setCurrentTourId } from '@/services/currentTour';
import { useRoundState } from '@/stores/roundState';
import { useDebugState } from '@/stores/debugState';
import { useDebugTraceState } from '@/stores/debugTraceState';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import {
  GAME_ICON_MAP,
  BunkerIcon,
  BirdieImage,
  GolfballIcon,
  HoleIllustration,
  MysteryPrizeIcon,
  PenaltyIcon,
  PuttButtonImage,
  ScoreButtonImage,
  ScoreButtonSelectedImage,
  ThreePuttIcon,
  TrophyImage,
  TrophyOnlyImage,
  WaterIcon,
} from '@/features/hole/assets';
import {
  compactPlayerName,
  computeNetForHole,
  computeStrokesReceivedForHole,
  hazardIconForGameName,
  hazardIconForType,
  hazardTypeFromName,
  isHazardGameName,
  isUuidLike,
  normalizeGameSlug,
  normalizeHoleEntry,
  normalizeHoleEvent,
  normalizePlayerKey,
  normalizeTotalsList,
  parseHandicapValue,
} from '@/features/hole/utils';
import type {
  DisplayTotal,
  GameDefinitionSummary,
  GameResultState,
  HoleEntry,
  HoleEventEntry,
  HoleMeta,
  Player,
  RoundSubgameEntry,
  ScoreTotal,
  Subgame,
  TeamSummary,
  TourLeaderboardResponse,
  SwipeItem,
} from '@/features/hole/types';
import { renderPuttLabel } from '@/features/hole/ui';
import { getGameDisplayName, getSubgameDisplayName } from '@/features/game-definitions/i18n';


type TourLeaderboardResponse = {
  tourId: string;
  totals: Array<{ playerId?: string; PlayerId?: string; totalPoints?: number; TotalPoints?: number }>;
};

type HoleEntry = {
  id: string;
  roundId: string;
  holeNumber: number;
  strokeIndex?: number;
  playerId?: string;
  playerName: string;
  grossStrokes: number | null;
  createdAt: string;
  handicapStrokes?: number;
  playingHandicap?: number | null;
  netStrokes?: number | null;
};
type HoleMeta = { par?: number; strokeIndex?: number; name?: string; description?: string; lengthMeters?: number };
type DisplayTotal = { key: string; name: string; totalStrokes: number; roundPoints?: number; pickups: number; isLeader?: boolean; roundRank?: number };
type NetResult = { netStrokes: number; strokesReceived: number; netVsPar?: number; netVsParLabel?: string };
type TeamSummary = { id: string; name?: string; players?: string[] };
type GameResultState = Record<string, { name?: string; scorePoints: Record<string, number> }>;
type GameDefinitionSummary = { id: string; name?: string; competitionType?: string };
type RoundSubgameEntry = { playerId: string; holeNumber: number; subgameId: string };
type HoleEventEntry = {
  playerId: string;
  holeNumber: number;
  eventType: string;
  eventValue?: string | null;
  occurredAt?: string | null;
};
type SwipeItem = {
  id: string;
  player?: Player;
  empty?: boolean;
  summary?: boolean;
  prevHole?: boolean;
  nextHole?: boolean;
};

type Subgame = { id: string; name: string; description?: string };

// Cache game results per round so navigating between holes keeps the latest scores visible.
const gameResultsCache: Record<string, GameResultState> = {};
// Cache totals per round so leaderboard stays populated while moving between holes.
const roundTotalsCache: Record<string, ScoreTotal[]> = {};
// Cache leaderboard totals per round to avoid mixing with stroke totals when switching holes.
const roundLeaderboardCache: Record<string, ScoreTotal[]> = {};
// Show "next hole mystery prize" popup once per user+round in current app runtime.
const seenNextMysteryPopupByUserRound = new Set<string>();

const renderPuttBalls = (count: number, marginRightOverride?: number, marginLeftOverride?: number) => (
  <NativeView
    style={[
      styles.puttBallRow,
      marginRightOverride !== undefined && { marginRight: marginRightOverride },
      marginLeftOverride !== undefined && { marginLeft: marginLeftOverride },
    ]}
  >
    {Array.from({ length: count }).map((_, index) => (
      <Image
        key={`putt-ball-${count}-${index}`}
        source={GolfballIcon}
        style={styles.puttBallIcon}
        resizeMode="contain"
      />
    ))}
  </NativeView>
);

export default function HoleScreen() {
  const {
    debug: debugParam,
    playerIndex: playerIndexParam,
    roundId: roundIdParam,
    hole: holeParam,
    layout: layoutParam,
  } = useLocalSearchParams<{
    debug?: string;
    playerIndex?: string;
    roundId?: string;
    hole?: string;
    layout?: string;
  }>();
  const { layout: layoutQueryParam } = useGlobalSearchParams<{ layout?: string }>();
  const router = useRouter();
  const pathname = usePathname();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const { user } = useUser();
  const safePathname = pathname ?? '';
  const isSwipeMode = safePathname.endsWith('/score');
  const isCompactMode = safePathname.endsWith('/compact');
  const swipeListRef = useRef<FlatList<SwipeItem> | null>(null);
  const storedRoundId = useRoundState((state) => state.roundId);
  const storedHole = useRoundState((state) => state.hole);
  const setRoundId = useRoundState((state) => state.setRoundId);
  const setHole = useRoundState((state) => state.setHole);
  const roundIdFromParams = useMemo(() => {
    const raw = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return raw ?? null;
  }, [roundIdParam]);
  const roundIdFromPath = useMemo(() => {
    if (!safePathname) return null;
    const parts = safePathname.split('/').filter(Boolean);
    const roundIndex = parts.indexOf('round');
    if (roundIndex < 0) return null;
    return parts[roundIndex + 1] ?? null;
  }, [safePathname]);
  const holeFromParams = useMemo(() => {
    const raw = Array.isArray(holeParam) ? holeParam[0] : holeParam;
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [holeParam]);
  const holeFromPath = useMemo(() => {
    if (!safePathname) return null;
    const parts = safePathname.split('/').filter(Boolean);
    const holeIndex = parts.indexOf('hole');
    if (holeIndex < 0) return null;
    const raw = parts[holeIndex + 1];
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [safePathname]);
  const debug = useMemo(() => {
    const raw = Array.isArray(debugParam) ? debugParam[0] : debugParam;
    return raw === 'true' || raw === '1';
  }, [debugParam]);
  const setDebugEnabled = useDebugState((state) => state.setEnabled);
  const holeViewTraceId = useDebugTraceState((state) => state.holeViewTraceId);
  const setHoleViewTraceId = useDebugTraceState((state) => state.setHoleViewTraceId);
  const setHoleScoreTraceId = useDebugTraceState((state) => state.setHoleScoreTraceId);
  const forceStandardLayout = useMemo(() => {
    const rawLocal = Array.isArray(layoutParam) ? layoutParam[0] : layoutParam;
    const rawQuery = Array.isArray(layoutQueryParam) ? layoutQueryParam[0] : layoutQueryParam;
    return rawLocal === 'standard' || rawQuery === 'standard';
  }, [layoutParam, layoutQueryParam]);
  const isHoleIndexPath = useMemo(() => {
    if (!safePathname) return false;
    const parts = safePathname.split('/').filter(Boolean);
    return parts.length === 4 && parts[0] === 'round' && parts[2] === 'hole';
  }, [safePathname]);
  const initialPageIndex = useMemo(() => {
    const raw = Array.isArray(playerIndexParam) ? playerIndexParam[0] : playerIndexParam;
    const parsed = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }, [playerIndexParam]);
  const [tourId, setTourId] = useState<string | undefined>(undefined);
  const roundId = roundIdFromParams ?? roundIdFromPath ?? storedRoundId;
  const holeNumber = holeFromParams ?? holeFromPath ?? storedHole ?? 1;
  const [players, setPlayers] = useState<Player[]>([]);
  const [scores, setScores] = useState<Record<string, string>>({});
  const [explicitScoreSelections, setExplicitScoreSelections] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState<ScoreTotal[]>([]);
  const [leaderboardTotals, setLeaderboardTotals] = useState<ScoreTotal[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [leaderboardGamePoints, setLeaderboardGamePoints] = useState<Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>>>(
    {}
  );
  const [tourTotals, setTourTotals] = useState<Record<string, number>>({});
  const [teams, setTeams] = useState<TeamSummary[]>([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [gameDefinitionTypes, setGameDefinitionTypes] = useState<Record<string, string>>({});
  const [gameDefinitionNames, setGameDefinitionNames] = useState<GameDefinitionSummary[]>([]);
  const [selectedGameDefinitionIds, setSelectedGameDefinitionIds] = useState<string[]>([]);
  const [holesCount, setHolesCount] = useState(18);
  const [holeEntries, setHoleEntries] = useState<HoleEntry[]>([]);
  const [socketClient, setSocketClient] = useState<BffWebSocketClient | null>(null);
  const [gameResults, setGameResults] = useState<GameResultState>({});
  const [strokeIndex, setStrokeIndex] = useState<number | null>(null);
  const [holeMeta, setHoleMeta] = useState<Record<number, HoleMeta>>({});
  const [holePar, setHolePar] = useState<number | null>(null);
  const [celebrating, setCelebrating] = useState(false);
  const [activePageIndex, setActivePageIndex] = useState(initialPageIndex);
  const hasSelectedGames = selectedGameDefinitionIds.length > 0;

  useEffect(() => {
    setDebugEnabled(debug);
  }, [debug, setDebugEnabled]);

  useEffect(() => {
    if (!roundId) return;
    setHoleViewTraceId(generateTraceId());
  }, [roundId, holeNumber, setHoleViewTraceId]);
  const activePageIndexRef = useRef(initialPageIndex);
  const autoNavigateNextHoleRef = useRef(false);
  const autoNavigatePrevHoleRef = useRef(false);
  const swipeItemsRef = useRef<SwipeItem[]>([]);
  const roundIdRef = useRef(roundId);
  const lastSyncedRoundIdRef = useRef<string | null>(null);
  const lastSyncedHoleRef = useRef<number | null>(null);
  const tourIdRef = useRef(tourId);
  const holeNumberRef = useRef(holeNumber);
  const holesCountRef = useRef(holesCount);
  const debugRef = useRef(false);
  const [isClient, setIsClient] = useState(Platform.OS !== 'web');
  const [showHoleDetails, setShowHoleDetails] = useState(false);
  const [eventCounts, setEventCounts] = useState<Record<string, { water: number; penalty: number; bunker: number; threePutt: number }>>({});
  const [eventPopups, setEventPopups] = useState<Record<string, { label: string; field: 'water' | 'penalty' | 'bunker' | 'threePutt' }>>({});
  const [roundSubgames, setRoundSubgames] = useState<RoundSubgameEntry[]>([]);
  const [subgames, setSubgames] = useState<Subgame[]>([]);
  const [mysteryModalOpen, setMysteryModalOpen] = useState(false);
  const [nextMysteryModalOpen, setNextMysteryModalOpen] = useState(false);
  const [nextMysteryModalRows, setNextMysteryModalRows] = useState<Array<{ subgame: string; player: string }>>([]);
  const [holeEvents, setHoleEvents] = useState<HoleEventEntry[]>([]);
  const [roundStateMessage, setRoundStateMessage] = useState<string | null>(null);
  const [scorePopupPlayerId, setScorePopupPlayerId] = useState<string | null>(null);
  const eventLabelTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const bottleScale = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (tourId) {
      setCurrentTourId(tourId, { preserveRound: true });
    }
  }, [tourId]);

  useEffect(() => {
    const nextRoundId = roundIdFromParams ?? roundIdFromPath;
    if (nextRoundId && nextRoundId !== lastSyncedRoundIdRef.current) {
      lastSyncedRoundIdRef.current = nextRoundId;
      if (nextRoundId !== storedRoundId) {
        setRoundId(nextRoundId);
      }
    }
  }, [roundIdFromParams, roundIdFromPath, setRoundId, storedRoundId]);

  useEffect(() => {
    const nextHole = holeFromParams ?? holeFromPath;
    if (nextHole && nextHole !== lastSyncedHoleRef.current) {
      lastSyncedHoleRef.current = nextHole;
      if (nextHole !== storedHole) {
        setHole(nextHole);
      }
    }
  }, [holeFromParams, holeFromPath, setHole, storedHole]);

  useEffect(() => {
    if (roundId && storedHole == null && !holeFromParams && !holeFromPath) {
      setHole(1);
    }
  }, [holeFromParams, holeFromPath, roundId, setHole, storedHole]);

  useEffect(() => {
    if (!isNavigationReady || !isFocused) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setSubscriptionContext({ roundId });
  }, [apiBaseUrl, isFocused, isNavigationReady, roundId]);

  useEffect(() => {
    autoNavigateNextHoleRef.current = false;
    autoNavigatePrevHoleRef.current = false;
    setNextMysteryModalOpen(false);
    setNextMysteryModalRows([]);
  }, [holeNumber]);

  useEffect(() => {
    roundIdRef.current = roundId;
    tourIdRef.current = tourId;
    holeNumberRef.current = holeNumber;
    holesCountRef.current = holesCount;
    debugRef.current = debug;
  }, [debug, holeNumber, holesCount, roundId, tourId]);

  useEffect(() => {
    if (!tourId || !user?.token) return;
    const loadTourTotals = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/tours/${tourId}/leaderboard`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const payload = (await response.json()) as TourLeaderboardResponse & {
          Totals?: Array<{ PlayerId?: string; TotalPoints?: number }>;
        };
        const normalized =
          payload.totals ??
          payload.Totals?.map((t) => ({
            playerId: t.PlayerId ?? '',
            totalPoints: t.TotalPoints ?? 0,
          })) ??
          [];
        const map: Record<string, number> = {};
        normalized.forEach((t) => {
          if (!t.playerId) return;
          map[t.playerId] = t.totalPoints ?? 0;
          const match = playersRef.current.find((p) => p.id === t.playerId);
          if (match?.name) map[match.name] = t.totalPoints ?? 0;
        });
        setTourTotals(map);
      } catch {
        // ignore
      }
    };
    loadTourTotals();
  }, [apiBaseUrl, tourId, user?.token]);
  useEffect(() => {
    if (!roundId || !isNavigationReady) return;
    const fetchSubgames = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/subgames`, {
          headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (!response.ok) {
          return;
        }
        const raw = (await response.json()) as Array<Record<string, unknown>>;
        const normalized = (raw ?? [])
          .map((item) => {
            const playerId = String(item.playerId ?? item.PlayerId ?? '');
            const subgameId = String(item.subgameId ?? item.SubgameId ?? '');
            const holeRaw = item.holeNumber ?? item.HoleNumber;
            const holeNumber = typeof holeRaw === 'number' ? holeRaw : Number(holeRaw);
            if (!playerId || !subgameId || !Number.isFinite(holeNumber)) {
              return null;
            }
            return {
              playerId,
              subgameId,
              holeNumber,
            } as RoundSubgameEntry;
          })
          .filter((entry): entry is RoundSubgameEntry => entry !== null);
        setRoundSubgames(normalized);
      } catch {
        setRoundSubgames([]);
      }
    };
    fetchSubgames();
  }, [apiBaseUrl, isNavigationReady, roundId, user?.token]);
  useEffect(() => {
    if (!roundId || !isNavigationReady) return;
    const fetchHoleEvents = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/hole-events`, {
          headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (!response.ok) {
          return;
        }
        const raw = (await response.json()) as Array<Record<string, unknown>>;
        const normalized = raw
          .map((item) => normalizeHoleEvent(item))
          .filter((item): item is HoleEventEntry => item !== null);
        setHoleEvents(normalized);
      } catch {
        setHoleEvents([]);
      }
    };
    fetchHoleEvents();
  }, [apiBaseUrl, isNavigationReady, roundId, user?.token]);
  useEffect(() => {
    if (!isNavigationReady) return;
    const fetchSubgameDefinitions = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/subgames`, {
          headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (!response.ok) {
          return;
        }
        const data = (await response.json()) as Subgame[];
        setSubgames(data ?? []);
      } catch {
        setSubgames([]);
      }
    };
    fetchSubgameDefinitions();
  }, [apiBaseUrl, isNavigationReady, user?.token]);
  const fireworksOpacity = useRef(new Animated.Value(0)).current;
  const finaleScale = useRef(new Animated.Value(0)).current;
  const [showFinaleOnly, setShowFinaleOnly] = useState(false);
  const [birdsVisible, setBirdsVisible] = useState(false);
  const birdieScale = useRef(new Animated.Value(0.05)).current;
  const birdieRotate = useRef(new Animated.Value(0)).current;
  const birdieOpacity = useRef(new Animated.Value(1)).current;
  const hasSyncedRoundRef = useRef(false);
  const lastHoleViewKeyRef = useRef<string | null>(null);
  const lastHoleViewSentAtRef = useRef<number>(0);
  const socketConnectionEpochRef = useRef(0);
  const lastHoleViewEpochRef = useRef(-1);
  const playersRef = useRef<Player[]>([]);
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        roundId,
        holeNumber,
        tourId,
        source: 'hole-ui',
        traceId: holeViewTraceId ?? undefined,
      }),
    [apiBaseUrl, holeNumber, holeViewTraceId, roundId, tourId, user?.token]
  );

  useEffect(() => {
    playersRef.current = players;
  }, [players]);
  useEffect(() => {
    if (Platform.OS === 'web') {
      setIsClient(true);
    }
  }, []);
  useEffect(() => {
    if (birdsVisible) {
      birdieScale.setValue(0.05);
      birdieRotate.setValue(0);
      birdieOpacity.setValue(1);
    }
  }, [birdsVisible, birdieOpacity, birdieRotate, birdieScale]);
  useEffect(() => {
    if (!isNavigationReady) return;
  }, [isNavigationReady]);

  useEffect(() => {
    setScores({});
    setExplicitScoreSelections({});
    setHolePar(null);
    setStrokeIndex(null);
    setRoundStateMessage(null);
  }, [holeNumber, roundId]);

  useEffect(() => {
    if (roundId && gameResultsCache[roundId]) {
      setGameResults(gameResultsCache[roundId]);
    } else {
      setGameResults({});
    }
    if (roundId && roundTotalsCache[roundId]) {
      setTotals(roundTotalsCache[roundId]);
    } else {
      setTotals([]);
    }
    if (roundId && roundLeaderboardCache[roundId]) {
      const cached = roundLeaderboardCache[roundId];
      setLeaderboardTotals(cached);
      const gamePointsMap: Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>> = {};
      cached.forEach((total) => {
        if (!total.playerId || !total.roundGamePoints) return;
        gamePointsMap[total.playerId] = total.roundGamePoints;
      });
      setLeaderboardGamePoints(gamePointsMap);
      const tourPointsMap: Record<string, number> = {};
      cached.forEach((total) => {
        if (total.tourGamesPoints === undefined) return;
        if (total.playerId) {
          tourPointsMap[total.playerId] = total.tourGamesPoints;
        }
        if (total.playerName) {
          tourPointsMap[total.playerName] = total.tourGamesPoints;
        }
      });
      if (Object.keys(tourPointsMap).length > 0) {
        setTourTotals((prev) => ({ ...prev, ...tourPointsMap }));
      }
    } else {
      setLeaderboardTotals([]);
      setLeaderboardGamePoints({});
    }
  }, [roundId]);

  useEffect(() => {
    if (!apiBaseUrl || !user?.token) {
      setGameDefinitionTypes({});
      return;
    }
    let cancelled = false;
    const fetchDefinitions = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/game-definitions`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        if (!response.ok) return;
        const payload = (await response.json()) as GameDefinitionSummary[];
        if (cancelled) return;
        const next: Record<string, string> = {};
        payload.forEach((def) => {
          if (!def?.id) return;
          const type = def.competitionType?.toLowerCase();
          if (type) {
            next[def.id] = type;
          }
        });
        setGameDefinitionTypes(next);
        setGameDefinitionNames(payload);
      } catch {
        if (!cancelled) {
          setGameDefinitionTypes({});
          setGameDefinitionNames([]);
        }
      }
    };
    fetchDefinitions();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, user?.token]);
  useEffect(() => {
    if (!roundId || !user?.token || !isNavigationReady) {
      setSelectedGameDefinitionIds([]);
      return;
    }
    let cancelled = false;
    const fetchRound = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as {
          gameDefinitionIds?: string[];
          GameDefinitionIds?: string[];
          holesCount?: number;
          HolesCount?: number;
        };
        if (cancelled) return;
        const ids = data.gameDefinitionIds ?? data.GameDefinitionIds ?? [];
        setSelectedGameDefinitionIds(ids);
        const nextHoles = data.holesCount ?? data.HolesCount;
        if (nextHoles === 9 || nextHoles === 18) {
          setHolesCount(nextHoles);
        } else {
          setHolesCount(18);
        }
      } catch {
        if (!cancelled) {
          setSelectedGameDefinitionIds([]);
          setHolesCount(18);
        }
      }
    };
    fetchRound();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isNavigationReady, roundId, user?.token]);

  const applyHandicapsFromMap = useCallback((mapObj: Record<string, unknown>) => {
    const map = new Map<string, number>();
    Object.entries(mapObj ?? {}).forEach(([k, v]) => {
      const num = typeof v === 'number' ? v : typeof v === 'string' ? parseInt(v, 10) : undefined;
      if (!Number.isFinite(num as number)) return;
      map.set(k, num as number);
    });
    if (map.size === 0) return;
    setPlayers((prev) => {
      let changed = false;
      const next = prev.map((p) => {
        const h = map.get(p.id) ?? map.get(p.name);
        if (h !== undefined && h !== p.handicap) {
          changed = true;
          return { ...p, handicap: h };
        }
        return p;
      });
      map.forEach((hcp, key) => {
        if (!next.some((p) => p.id === key || p.name === key)) {
          next.push({ id: key, name: key, createdAt: '', handicap: hcp });
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const applyHandicapsFromEntries = useCallback((entries: HoleEntry[]) => {
    const map: Record<string, number> = {};
    entries.forEach((e) => {
      if (e.playingHandicap !== null && e.playingHandicap !== undefined) {
        const key = e.playerId ?? e.playerName;
        if (key) {
          map[key] = e.playingHandicap;
        }
      }
    });
    if (Object.keys(map).length === 0) return;
    applyHandicapsFromMap(map);
  }, [applyHandicapsFromMap]);

  useEffect(() => {
    const meta = holeMeta[holeNumber];
    if (meta?.par !== undefined) {
      setHolePar(meta.par ?? null);
    }
    if (meta?.strokeIndex !== undefined) {
      setStrokeIndex(meta.strokeIndex ?? null);
    }
  }, [holeMeta, holeNumber]);

  useEffect(() => {
    if (!holeEntries.length) return;
    const mapped: Record<string, string> = {};
    holeEntries.forEach((e) => {
      const val = e.grossStrokes === null ? 'Pickup' : String(e.grossStrokes);
      const key = e.playerId ?? e.playerName;
      if (key) {
        mapped[key] = val;
      }
      if (e.playerName) {
        mapped[e.playerName] = val;
      }
    });
    setScores(mapped);
  }, [holeEntries, holeNumber]);

  const roundIdIsValid = useMemo(
    () => !!roundId && roundId !== '00000000-0000-0000-0000-000000000000',
    [roundId]
  );
  const formatLogMessage = useCallback((event: string, payload: Record<string, unknown>) => {
    const detailKeys = [
      'roundId',
      'tourId',
      'hole',
      'incomingRound',
      'expectedRound',
      'gameId',
      'playerId',
      'playerKey',
      'points',
      'games',
      'results',
      'entries',
      'totals',
      'joined',
      'requested',
      'type',
      'par',
      'strokeIndex',
      'updatedHoleNumber',
      'updatedPlayerId',
    ];
    const detailParts = detailKeys
      .filter((key) => key in payload)
      .map((key) => `${key}=${String(payload[key])}`);
    const messageText = typeof payload.message === 'string' ? payload.message : '';
    const prefix = messageText ? `${event} - ${messageText}` : event;
    return detailParts.length > 0 ? `${prefix} | ${detailParts.join(' ')}` : prefix;
  }, []);
  const baseLogContext = useMemo(
    () => ({ screen: 'hole', roundId, tourId, hole: holeNumber }),
    [roundId, tourId, holeNumber]
  );
  const logWithContext = useCallback(
    (event: string, payload: Record<string, unknown>, level?: 'debug' | 'warn' | 'error') => {
      const merged = { ...baseLogContext, ...payload };
      logToBff(formatLogMessage(event, merged), merged, level);
    },
    [baseLogContext, formatLogMessage, logToBff]
  );
  const logWithContextRef = useRef(logWithContext);
  useEffect(() => {
    logWithContextRef.current = logWithContext;
  }, [logWithContext]);
  const navigateToHole = useCallback(
    (targetHole: number, options: { swipe: boolean; compact?: boolean; playerIndex?: number }) => {
      if (!roundId) return;
      setHole(targetHole);
      const params: string[] = [];
      if (debug) params.push('debug=true');
      if (options.swipe && typeof options.playerIndex === 'number') {
        params.push(`playerIndex=${options.playerIndex}`);
      }
      const query = params.length ? `?${params.join('&')}` : '';
      const basePath = options.swipe
        ? `/round/${roundId}/hole/${targetHole}/score`
        : options.compact
          ? `/round/${roundId}/hole/${targetHole}/compact`
          : `/round/${roundId}/hole/${targetHole}`;
      router.push(`${basePath}${query}`);
    },
    [debug, roundId, router, setHole]
  );
  const scorePopupPlayer = useMemo(
    () => players.find((player) => player.id === scorePopupPlayerId) ?? null,
    [players, scorePopupPlayerId]
  );
  useEffect(() => {
    if (isSwipeMode || isCompactMode || !roundId || !isHoleIndexPath || forceStandardLayout) return;
    const params: string[] = [];
    if (debug) params.push('debug=true');
    const query = params.length ? `?${params.join('&')}` : '';
    router.replace(`/round/${roundId}/hole/${holeNumber}/compact${query}`);
  }, [debug, forceStandardLayout, holeNumber, isCompactMode, isHoleIndexPath, isSwipeMode, roundId, router]);
  const updateCompactHazardCount = useCallback(
    (player: Player, field: 'water' | 'penalty' | 'bunker' | 'threePutt', delta: number) => {
      const eventKey = `${player.id}:${holeNumber}`;
      let nextValue = 0;
      setEventCounts((prev) => {
        const next = { ...prev };
        const current = next[eventKey] ?? { water: 0, penalty: 0, bunker: 0, threePutt: 0 };
        nextValue = Math.max(0, current[field] + delta);
        next[eventKey] = { ...current, [field]: nextValue };
        return next;
      });
      setEventPopups((prev) => ({
        ...prev,
        [eventKey]: { label: t(`hole.event.${field}`), field },
      }));
      const existingTimeout = eventLabelTimeoutRef.current[eventKey];
      if (existingTimeout) clearTimeout(existingTimeout);
      eventLabelTimeoutRef.current[eventKey] = setTimeout(() => {
        setEventPopups((prev) => {
          const next = { ...prev };
          delete next[eventKey];
          return next;
        });
      }, 3000);
      if (socketClient && roundIdIsValid && roundId) {
        const sent = socketClient.send({
          type: 'hole.event',
          roundId,
          playerId: player.id,
          holeNumber,
          eventType: field,
          eventValue: String(nextValue),
          eventDelta: delta,
          timestamp: new Date().toISOString(),
        });
        if (!sent) {
          logWithContext(
            'hole.event.sendFailed',
            { message: t('hole.eventSendFailed'), roundId, hole: holeNumber, playerId: player.id, eventType: field },
            'warn'
          );
        }
      }
    },
    [holeNumber, logWithContext, roundId, roundIdIsValid, socketClient]
  );

  const finishRound = useCallback(() => {
    if (!socketClient || !roundId) return;
    const sent = socketClient.send({ type: 'round.status.update', roundId, status: 'finished' });
    if (!sent) {
      logWithContext(
        'hole.round.finishSendFailed',
        { message: t('hole.finishSendFailed'), roundId },
        'warn'
      );
    }
    const params: string[] = ['from=hole'];
    if (debug) params.push('debug=true');
    const query = params.length ? `?${params.join('&')}` : '';
    router.push(`/round/${roundId}/summary${query}`);
  }, [debug, logWithContext, roundId, router, socketClient]);

  useEffect(() => {
    if (!socketClient || !roundIdIsValid || !roundId) return;
    const holeViewKey = `${roundId}:${holeNumber}`;
    const connectionEpoch = socketConnectionEpochRef.current;
    const shouldSend =
      lastHoleViewKeyRef.current !== holeViewKey || lastHoleViewEpochRef.current !== connectionEpoch;
    if (!shouldSend) return;
    lastHoleViewKeyRef.current = holeViewKey;
    lastHoleViewEpochRef.current = connectionEpoch;
    lastHoleViewSentAtRef.current = Date.now();
    socketClient.send({ type: 'hole.view', roundId, hole: holeNumber });
    logWithContextRef.current('hole.view.sent', { message: 'Sent hole.view request to BFF.' });
  }, [socketClient, roundIdIsValid, roundId, holeNumber]);

  useEffect(() => {
    if (!user || !isNavigationReady || !roundId) return;
    const fetchPlayers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId ?? ''}/players`, {
          headers: user.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (!response.ok) {
          throw new Error();
        }
        const data = await response.json();
        const list = (Array.isArray(data) ? data : (data.players ?? data.Players ?? [])) as Array<Player & Record<string, unknown>>;
        const normalizedPlayers = list.map((p) => {
          const raw = p as {
            handicap?: unknown;
            Handicap?: unknown;
            hcp?: unknown;
            Hcp?: unknown;
            playingHandicap?: unknown;
            playing_handicap?: unknown;
            imageKey?: string | null;
            ImageKey?: string | null;
            imageThumbKey?: string | null;
            ImageThumbKey?: string | null;
          };
          const handicap = parseHandicapValue(
            raw.handicap ?? raw.Handicap ?? raw.hcp ?? raw.Hcp ?? raw.playingHandicap ?? raw.playing_handicap
          );
          const imageKey = raw.imageKey ?? raw.ImageKey ?? (p as any).imageKey ?? (p as any).ImageKey ?? null;
          const imageThumbKey =
            raw.imageThumbKey ?? raw.ImageThumbKey ?? (p as any).imageThumbKey ?? (p as any).ImageThumbKey ?? null;
          return { ...p, handicap, imageKey, imageThumbKey };
        });
        setPlayers(normalizedPlayers);
        const incomingTourId = (data as { tourId?: string; TourId?: string }).tourId ?? (data as { TourId?: string }).TourId;
        if (incomingTourId) {
          setTourId(incomingTourId);
        }
      } catch {
        setError('Kunde inte hämta spelare.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers().catch(() => setError('Kunde inte hämta spelare.'));
  }, [apiBaseUrl, user, isNavigationReady, roundId]);

  useEffect(() => {
    if (!user || !isNavigationReady || !roundIdIsValid || !roundId) return;
    const fetchTeams = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/teams?roundId=${roundId}`, {
          headers: user.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (!response.ok) {
          throw new Error(`Failed to load teams (${response.status})`);
        }
        const data = (await response.json()) as TeamSummary[];
        setTeams(Array.isArray(data) ? data : []);
        logWithContext('hole.teams.loaded', {
          message: 'Loaded teams for round.',
          teamCount: Array.isArray(data) ? data.length : 0,
        });
      } catch (err) {
        logWithContext(
          'hole.teams.error',
          { message: err instanceof Error ? err.message : 'Failed to load teams.' },
          'warn'
        );
        setTeams([]);
      } finally {
        setTeamsLoaded(true);
      }
    };

    fetchTeams().catch(() =>
      logWithContext('hole.teams.error', { message: 'Failed to load teams.' }, 'warn')
    );
  }, [apiBaseUrl, isNavigationReady, logWithContext, roundId, roundIdIsValid, user]);

  const teamRedirectedRef = useRef(false);

  useEffect(() => {
    if (!roundId || !isNavigationReady) return;
    if (!teamsLoaded) return;
    if (!selectedGameDefinitionIds.length) return;
    if (teams.length > 0) return;
    const hasTeamGame = selectedGameDefinitionIds.some(
      (gameId) => (gameDefinitionTypes[gameId] ?? '').toLowerCase() === 'team'
    );
    if (!hasTeamGame) return;
    if (teamRedirectedRef.current) return;
    teamRedirectedRef.current = true;
    const debugSuffix = debug ? '?debug=true' : '';
    router.push(`/round/${roundId}/team-select${debugSuffix}`);
  }, [debug, gameDefinitionTypes, isNavigationReady, roundId, router, selectedGameDefinitionIds, teams, teamsLoaded]);

  useEffect(() => {
    if (!user || !isNavigationReady) return;
    // Scores are delivered over WebSocket (round.state / hole.update); no HTTP fallback.
  }, [user, isNavigationReady]);

  useEffect(() => {
    if (!roundId || !user?.token || !isNavigationReady) return;
    const client = getSharedBffWebSocket(apiBaseUrl);
    client.setAuthToken(user.token);
    setSocketClient(client);
    
    const resubscribeRound = () => {
      const joined = client.send({ type: 'round.join', roundId });
      const requested = client.send({ type: 'round.requestState', roundId });
      hasSyncedRoundRef.current = true;
      logWithContextRef.current('hole.round.resubscribe', {
        message: 'Requested round.join and round.requestState.',
        joined,
        requested,
      });
    };
    const unsubscribeMessages = client.onMessage((evt) => {
      const evtType = (evt as { type?: string }).type;
      logWithContext(
        `hole.ws.message.${evtType ?? 'unknown'}`,
        { message: 'Received websocket message.', type: evtType },
        'debug'
      );
      if ((evt as { type?: string }).type === 'round.state') {
        logWithContext('hole.ws.round.state.raw', { message: 'Raw round.state payload.' }, 'debug');
      }
      if ((evt as HoleUpdateEvent).type === 'hole.update' && (evt as HoleUpdateEvent).roundId === roundId) {
        const holeEvent = evt as HoleUpdateEvent;
        logWithContext(
          'hole.event.update',
          {
            message: 'Applied hole.update event.',
            hole: holeEvent.hole,
            totals: holeEvent.totals?.length ?? 0,
            entries: holeEvent.entries?.length ?? 0,
            strokeIndex: holeEvent.strokeIndex,
            par: holeEvent.par,
          },
          'debug'
        );
        const playingHandicapsMap = (holeEvent as unknown as { playingHandicaps?: Record<string, unknown>; handicaps?: Record<string, unknown> }).playingHandicaps ??
          (holeEvent as unknown as { handicaps?: Record<string, unknown> }).handicaps;
        if (playingHandicapsMap) {
          applyHandicapsFromMap(playingHandicapsMap as Record<string, unknown>);
        }
        if (holeEvent.entries) {
          const filtered = holeEvent.entries
            .map((e) =>
              normalizeHoleEntry({
                ...e,
                holeNumber: holeEvent.hole,
                strokeIndex: holeEvent.strokeIndex ?? null,
              } as Record<string, unknown>)
            );
          setHoleEntries(filtered);
          applyHandicapsFromEntries(filtered);
          const firstIdx =
            holeEvent.strokeIndex ??
            filtered.find((e) => e.strokeIndex !== undefined && e.strokeIndex !== null)?.strokeIndex ??
            null;
          setStrokeIndex(firstIdx);
          setHoleMeta((prev) => {
            const current = prev[holeNumber] ?? {};
            const strokeIndex = firstIdx ?? current.strokeIndex;
            const par = holeEvent.par ?? current.par;
            return { ...prev, [holeNumber]: { ...current, strokeIndex, par } };
          });
          const mapped: Record<string, string> = {};
          filtered.forEach((e) => {
            const gross = e.grossStrokes ?? null;
            const val = gross === null ? 'Pickup' : String(gross);
            const key = e.playerId ?? e.playerName;
            if (key) mapped[key] = val;
            if (e.playerName) mapped[e.playerName] = val;
          });
          setScores(mapped);
        }
        if (holeEvent.totals) {
          setTotals(normalizeTotalsList(holeEvent.totals as ScoreTotal[], playersRef.current));
        }
      } else if ((evt as RoundStateEvent).type === 'round.state' && (evt as RoundStateEvent).roundId === roundId) {
        const roundState = evt as RoundStateEvent;
        try {
          setRoundStateMessage(JSON.stringify(roundState, null, 2));
        } catch {
          setRoundStateMessage(null);
        }
        if (roundState.holes) {
          setHoleMeta((prev) => {
            const next = { ...prev };
            roundState.holes?.forEach((h) => {
              const prior = next[h.holeNumber] ?? {};
              const strokeIndex = h.strokeIndex ?? h.index ?? prior.strokeIndex;
              next[h.holeNumber] = {
                ...prior,
                par: h.par ?? prior.par,
                strokeIndex,
                name: h.name ?? prior.name,
                description: h.description ?? prior.description,
              };
            });
            return next;
          });
        }
        if (roundState.holeEntries) {
          const filtered = roundState.holeEntries
            .filter((h) => h.holeNumber === holeNumber)
            .map((h) => normalizeHoleEntry(h as Record<string, unknown>));
          setHoleEntries(filtered);
          applyHandicapsFromEntries(filtered);
        }
        if (roundState.totals) {
          const normalizedTotals = normalizeTotalsList(roundState.totals as ScoreTotal[], playersRef.current);
          setTotals(normalizedTotals);
          if (roundId) {
            roundTotalsCache[roundId] = normalizedTotals;
          }
        }
      } else if ((evt as HoleInfoEvent).type === 'hole.info') {
        const info = evt as HoleInfoEvent;
        logWithContext(
          'hole.event.info',
          {
            message: 'Received hole.info.',
            hole: info.hole,
            par: info.par,
            strokeIndex: info.strokeIndex,
            name: info.name,
            lengthMeters: info.lengthMeters,
          },
          'debug'
        );
        if (info.hole === holeNumber) {
          setHoleMeta((prev) => {
            const current = prev[holeNumber] ?? {};
            return {
              ...prev,
              [holeNumber]: {
                ...current,
                par: info.par ?? current.par,
                strokeIndex: info.strokeIndex ?? current.strokeIndex,
                name: info.name ?? current.name,
                description: info.description ?? current.description,
                lengthMeters: info.lengthMeters ?? current.lengthMeters,
              },
            };
          });
          setHolePar((info.par ?? null) ?? null);
          setStrokeIndex((info.strokeIndex ?? null) ?? null);
        }
      } else if (((evt as HoleUpdateEvent).type === 'hole.update' || (evt as { type?: string }).type === 'hole.updated') && (evt as HoleUpdateEvent).roundId === roundId) {
        const holeEvent = evt as HoleUpdateEvent;
        logWithContext(
          'hole.event.updated',
          {
            message: 'Received hole.updated or secondary hole.update.',
            hole: holeEvent.hole,
            totals: holeEvent.totals?.length ?? 0,
            entries: holeEvent.entries?.length ?? 0,
            par: holeEvent.par,
            strokeIndex: holeEvent.strokeIndex,
          },
          'debug'
        );
      const entries = holeEvent.entries?.map((e) => normalizeHoleEntry(e as Record<string, unknown>)) ?? [];
      if (holeEvent.hole === holeNumber) {
        setHoleEntries(entries);
        applyHandicapsFromEntries(entries);
        if (holeEvent.par !== undefined && holeEvent.par !== null) setHolePar(holeEvent.par);
        if (holeEvent.strokeIndex !== undefined && holeEvent.strokeIndex !== null) setStrokeIndex(holeEvent.strokeIndex);
      }
      if (holeEvent.totals) {
        const normalizedTotals = normalizeTotalsList(holeEvent.totals as ScoreTotal[], playersRef.current);
        setTotals(normalizedTotals);
        if (roundId) {
          roundTotalsCache[roundId] = normalizedTotals;
        }
      }
      // Ask for the latest round state to backfill any missed results/holes.
      if (client && roundId) {
        client.send({ type: 'round.requestState', roundId });
      }
      } else if ((evt as { type?: string }).type === 'round.leaderboard' && (evt as { roundId?: string }).roundId === roundId) {
        const lbTotals = (evt as { totals?: ScoreTotal[] }).totals;
        if (lbTotals) {
          const normalizedTotals = normalizeTotalsList(lbTotals as ScoreTotal[], playersRef.current);
          setLeaderboardTotals(normalizedTotals);
          const gamePointsMap: Record<string, Array<{ gameId?: string; roundGamePoints: number; position?: number }>> = {};
          normalizedTotals.forEach((total) => {
            if (!total.roundGamePoints) return;
            if (total.playerId) {
              gamePointsMap[total.playerId] = total.roundGamePoints;
            }
            if (total.playerName) {
              gamePointsMap[total.playerName] = total.roundGamePoints;
            }
          });
          setLeaderboardGamePoints(gamePointsMap);
          const tourPointsMap: Record<string, number> = {};
          normalizedTotals.forEach((total) => {
            if (total.tourGamesPoints === undefined) return;
            if (total.playerId) {
              tourPointsMap[total.playerId] = total.tourGamesPoints;
            }
            if (total.playerName) {
              tourPointsMap[total.playerName] = total.tourGamesPoints;
            }
          });
          if (Object.keys(tourPointsMap).length > 0) {
            setTourTotals((prev) => ({ ...prev, ...tourPointsMap }));
          }
          if (roundId) {
            roundLeaderboardCache[roundId] = normalizedTotals;
          }
        }
      } else if ((evt as { type?: string }).type === 'game.result') {
        const aggregatedEvent = evt as GameResultsEvent;
        const incomingRound = aggregatedEvent.roundId;
        const incomingGameId = aggregatedEvent.games?.[0]?.gameId ?? 'game';
        logWithContext(
          'hole.game.result.received',
          {
            message: 'Received game.result.',
            incomingRound,
            expectedRound: roundId,
            gameId: incomingGameId,
            updatedHoleNumber: aggregatedEvent.updatedHoleNumber,
            updatedPlayerId: aggregatedEvent.updatedPlayerId,
            games: aggregatedEvent.games?.length ?? 0,
          },
          'debug'
        );
        if (incomingRound !== roundId) {
          logWithContext(
            'hole.game.result.skipped',
            {
              message: 'Skipped game.result for other round.',
              incomingRound,
              expectedRound: roundId,
              gameId: incomingGameId,
            },
            'warn'
          );
          return;
        }
        const aggregated: GameResultState = {};
        aggregatedEvent.games.forEach((game) => {
          const gameId = game.gameId ?? 'game';
          const current = aggregated[gameId] ?? { name: game.gameName ?? 'Game', scorePoints: {} };
          (game.players ?? []).forEach((player) => {
            const matchingPlayer = playersRef.current.find((p) => p.id === player.playerId || p.name === player.playerId);
            const playerKey = matchingPlayer?.id ?? player.playerId;
            const playerNameKey = matchingPlayer?.name && matchingPlayer.name !== playerKey ? matchingPlayer.name : null;
            const normIdKey = normalizePlayerKey(playerKey);
            const normNameKey = normalizePlayerKey(playerNameKey);
            if (playerKey) current.scorePoints[playerKey] = player.lastScorePoints;
            if (playerNameKey) current.scorePoints[playerNameKey] = player.lastScorePoints;
            if (normIdKey) current.scorePoints[normIdKey] = player.lastScorePoints;
            if (normNameKey) current.scorePoints[normNameKey] = player.lastScorePoints;
          });
          current.name = current.name ?? game.gameName ?? 'Game';
          aggregated[gameId] = current;
        });
        setGameResults(aggregated);
        if (roundId) {
          gameResultsCache[roundId] = aggregated;
        }
        logWithContext(
          'hole.game.result.applied',
          {
            message: 'Applied aggregated game.result to local gameResults.',
            roundId,
            games: Object.keys(aggregated).length,
            gameIds: Object.keys(aggregated),
          },
          'debug'
        );
      }
    });

    const unsubscribeConnection = client.onConnectionChange((state) => {
      if (state === 'connected') {
        setError(null);
        socketConnectionEpochRef.current += 1;
        hasSyncedRoundRef.current = false;
        if (!hasSyncedRoundRef.current) {
          resubscribeRound();
        }
        const currentRoundId = roundIdRef.current;
        const currentHole = holeNumberRef.current;
        if (currentRoundId) {
          const holeViewKey = `${currentRoundId}:${currentHole}`;
          const connectionEpoch = socketConnectionEpochRef.current;
          const shouldSendHoleView =
            lastHoleViewKeyRef.current !== holeViewKey || lastHoleViewEpochRef.current !== connectionEpoch;
          if (shouldSendHoleView) {
            lastHoleViewKeyRef.current = holeViewKey;
            lastHoleViewEpochRef.current = connectionEpoch;
            lastHoleViewSentAtRef.current = Date.now();
            client.send({ type: 'hole.view', roundId: currentRoundId, hole: currentHole });
            logWithContextRef.current('hole.view.sent', { message: 'Sent hole.view request to BFF after reconnect.' });
          }
        }
      } else {
        hasSyncedRoundRef.current = false;
      }
    });

    const unsubscribeErrors = client.onError((message) => setError(message));

    resubscribeRound();

    return () => {
      client.send({ type: 'round.leave', roundId });
      hasSyncedRoundRef.current = false;
      unsubscribeMessages();
      unsubscribeConnection();
      unsubscribeErrors();
      logWithContextRef.current('hole.round.leave', { message: 'Sent round.leave for cleanup.' }, 'debug');
    };
  }, [apiBaseUrl, roundId, user?.token, isNavigationReady, applyHandicapsFromEntries, applyHandicapsFromMap]);

  const handleScore = async (player: Player, value: string) => {
    if (!roundId) return;
    if (!socketClient) {
      setError('Ingen anslutning till BFF:en. Försök igen.');
      return;
    }
    const traceId = generateTraceId();
    setHoleScoreTraceId(traceId);
    const parToSend = holePar ?? currentHoleMeta.par ?? 4;
    if (!holePar && !currentHoleMeta.par) {
      logToBff('hole.birdie.par.missing', { holeNumber, parToSend, value }, 'warn');
    }
    const numericValue = Number.parseInt(value, 10);
    if (!Number.isNaN(numericValue)) {
      if (numericValue === parToSend - 1) {
        triggerBirdies();
      }
      logToBff('hole.birdie.handleScore', { scorePopupPlayerId: scorePopupPlayerId ?? null, value }, 'info');
    }
    setScores((prev) => ({ ...prev, [player.name]: value, [player.id]: value }));
    setExplicitScoreSelections((prev) => ({ ...prev, [player.name]: true, [player.id]: true }));

    try {
      const body =
        value === 'Pickup'
          ? {
              roundId,
              playerId: player.id,
              hole: holeNumber,
              par: parToSend,
              strokes: null,
              pickup: true,
              timestamp: new Date().toISOString(),
              traceId,
            }
          : {
              roundId,
              playerId: player.id,
              hole: holeNumber,
              par: parToSend,
              strokes: parseInt(value, 10),
              pickup: false,
              timestamp: new Date().toISOString(),
              traceId,
            };
      const sent = socketClient.sendScoreUpdate(body);
      if (!sent) {
        throw new Error('Kunde inte nå BFF via websocket.');
      }

      const popupSeenKey = `${user?.id ?? 'unknown'}:${roundId}:mystery-next-hole`;
      const nextHole = holeNumber + 1;
      if (nextHole <= holesCount) {
        const upcomingEntries = roundSubgames.filter((entry) => entry.holeNumber === nextHole);
        if (upcomingEntries.length > 0) {
          if (!seenNextMysteryPopupByUserRound.has(popupSeenKey)) {
            const rows = upcomingEntries
              .map((entry) => ({
                subgame: subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame'),
                player: playerNameById[entry.playerId] ?? t('hole.mysteryPrizeUnknownPlayer'),
              }))
              .sort((a, b) => {
                const bySubgame = a.subgame.localeCompare(b.subgame);
                if (bySubgame !== 0) return bySubgame;
                return a.player.localeCompare(b.player);
              });
            setNextMysteryModalRows(rows);
            setNextMysteryModalOpen(true);
            seenNextMysteryPopupByUserRound.add(popupSeenKey);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte spara resultatet.');
    }
  };

  const triggerCelebration = () => {
    setCelebrating(true);
    bottleScale.setValue(0.2);
    fireworksOpacity.setValue(0);
    finaleScale.setValue(0);
    setShowFinaleOnly(false);
    Animated.timing(bottleScale, {
      toValue: 7,
      duration: 900,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    }).start(() => {
      setTimeout(() => {
        setShowFinaleOnly(true);
        Animated.sequence([
          Animated.timing(fireworksOpacity, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
          }),
          Animated.timing(finaleScale, {
            toValue: 2.4,
            duration: 400,
            easing: Easing.out(Easing.ease),
            useNativeDriver: true,
          }),
        ]).start(() => {
          setTimeout(() => {
            finaleScale.setValue(0);
            setCelebrating(false);
            setShowFinaleOnly(false);
          }, 3000);
        });
      }, 3000);
    });
  };

  const triggerBirdies = () => {
    setBirdsVisible(true);
    birdieScale.setValue(0.05);
    birdieRotate.setValue(0);
    birdieOpacity.setValue(1);
    logToBff('hole.birdie.trigger', { holeNumber }, 'info');
    const baseSize = 100;
    const fillScale = windowWidth / baseSize;
    const blowScale = fillScale;

    const startAnimation = () => {
      Animated.sequence([
        Animated.parallel([
          Animated.timing(birdieScale, {
            toValue: fillScale,
            duration: 2300,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
          }),
          Animated.timing(birdieRotate, {
            toValue: 1,
            duration: 2300,
            easing: Easing.linear,
            useNativeDriver: false,
          }),
        ]),
        Animated.delay(1000),
        Animated.parallel([
          Animated.timing(birdieScale, {
            toValue: blowScale,
            duration: 300,
            easing: Easing.out(Easing.quad),
            useNativeDriver: false,
          }),
          Animated.timing(birdieOpacity, {
            toValue: 0,
            duration: 300,
            easing: Easing.out(Easing.quad),
            useNativeDriver: false,
          }),
        ]),
      ]).start(() => {
        setBirdsVisible(false);
        birdieScale.setValue(0.05);
        birdieRotate.setValue(0);
        birdieOpacity.setValue(1);
      });
    };

    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(startAnimation);
    } else {
      setTimeout(startAnimation, 0);
    }
  };

  const currentHoleMeta = holeMeta[holeNumber] ?? {};
  const parLabel = holePar ?? currentHoleMeta.par ?? '–';
  const hcpLabel = strokeIndex ?? currentHoleMeta.strokeIndex ?? '–';
  const parForHole = holePar ?? currentHoleMeta.par ?? null;
  const strokeIndexForHole = strokeIndex ?? currentHoleMeta.strokeIndex ?? null;
  const hasMysteryPrizeForHole = useMemo(
    () => roundSubgames.some((entry) => entry.holeNumber === holeNumber),
    [roundSubgames, holeNumber]
  );
  const mysteryEntriesForHole = useMemo(
    () => roundSubgames.filter((entry) => entry.holeNumber === holeNumber),
    [roundSubgames, holeNumber]
  );
  const playerNameById = useMemo(() => {
    const map: Record<string, string> = {};
    players.forEach((player) => {
      map[player.id] = player.name;
    });
    return map;
  }, [players]);
  const subgameNameById = useMemo(() => {
    const map: Record<string, string> = {};
    subgames.forEach((subgame) => {
      map[subgame.id] = getSubgameDisplayName(subgame, t('hole.mysteryPrizeUnknownSubgame'));
    });
    return map;
  }, [subgames]);
  const selectedHazardTypes = useMemo(() => {
    if (selectedGameDefinitionIds.length === 0) return new Set<string>();
    const selected = new Set(selectedGameDefinitionIds);
    const types = gameDefinitionNames
      .filter((def) => def.id && selected.has(def.id))
      .map((def) => hazardTypeFromName(def.name ?? (def as { Name?: string }).Name))
      .filter((value): value is string => Boolean(value));
    return new Set(types);
  }, [gameDefinitionNames, selectedGameDefinitionIds]);
  const isStablefordGame = useMemo(() => {
    const selected = new Set(selectedGameDefinitionIds);
    const fromDefinitions = gameDefinitionNames.some((def) => {
      if (!def.id || (selected.size > 0 && !selected.has(def.id))) return false;
      const name = (def.name ?? (def as { Name?: string }).Name ?? '').toLowerCase();
      return name.includes('stableford');
    });
    if (fromDefinitions) return true;
    return Object.values(gameResults).some((game) => (game.name ?? '').toLowerCase().includes('stableford'));
  }, [gameDefinitionNames, gameResults, selectedGameDefinitionIds]);
  const penaltyGameId = useMemo(() => {
    const match = gameDefinitionNames.find((def) => {
      const name = def.name ?? (def as { Name?: string }).Name ?? '';
      return name.trim().toLowerCase() === 'penalty';
    });
    return match?.id ?? '6b9c72e0-6c1c-4d17-9f1f-6c0e2f71a742';
  }, [gameDefinitionNames]);
  const showPenaltyEvent = useMemo(
    () => selectedGameDefinitionIds.includes(penaltyGameId),
    [penaltyGameId, selectedGameDefinitionIds]
  );
  const showWaterEvent = useMemo(() => selectedHazardTypes.has('water'), [selectedHazardTypes]);
  const showBunkerEvent = useMemo(() => selectedHazardTypes.has('bunker'), [selectedHazardTypes]);
  const showThreePuttEvent = useMemo(() => selectedHazardTypes.has('threeputt'), [selectedHazardTypes]);
  const mysteryWinnersBySubgame = useMemo(() => {
    const map: Record<string, { playerId: string; occurredAt: string | null }> = {};
    holeEvents
      .filter((event) => event.eventType === 'mysteryWinner' && event.holeNumber === holeNumber)
      .forEach((event) => {
        if (!event.eventValue) return;
        map[event.eventValue] = {
          playerId: event.playerId,
          occurredAt: event.occurredAt ?? null,
        };
      });
    return map;
  }, [holeEvents, holeNumber]);
  const mysteryWinnerIdsForHole = useMemo(() => {
    return new Set(Object.values(mysteryWinnersBySubgame).map((entry) => entry.playerId));
  }, [mysteryWinnersBySubgame]);
  const mysteryPrizeText = useMemo(() => {
    const entries = mysteryEntriesForHole;
    if (entries.length === 0) {
      return t('hole.mysteryPrizeNone');
    }
    const parts = entries.map((entry) => {
      const subgameName = subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame');
      const playerName = playerNameById[entry.playerId] ?? t('hole.mysteryPrizeUnknownPlayer');
      const winnerId = mysteryWinnersBySubgame[entry.subgameId]?.playerId;
      if (winnerId) {
        const winnerName = playerNameById[winnerId] ?? t('hole.mysteryPrizeUnknownPlayer');
        return t('hole.mysteryPrizeEntryWinner', { subgame: subgameName, player: playerName, winner: winnerName });
      }
      return t('hole.mysteryPrizeEntry', { subgame: subgameName, player: playerName });
    });
    return `${t('hole.mysteryPrizeTitle')}: ${parts.join(', ')}`;
  }, [mysteryEntriesForHole, mysteryWinnersBySubgame, playerNameById, subgameNameById]);
  const handleMysteryWinnerSelect = useCallback(
    (subgameId: string, winnerId: string) => {
      if (!roundId) return;
      const entry = mysteryEntriesForHole.find((item) => item.subgameId === subgameId);
      if (!entry) return;
      const socket = socketClient ?? getSharedBffWebSocket(apiBaseUrl);
      socket.setAuthToken(user?.token ?? null);
      const timestamp = new Date().toISOString();
      const sent = socket.send({
        type: 'hole.event',
        roundId,
        playerId: winnerId,
        holeNumber: entry.holeNumber,
        eventType: 'mysteryWinner',
        eventValue: subgameId,
        timestamp,
      });
      if (!sent) {
        setError('Kunde inte spara mystery prize-vinnare.');
        return;
      }
      socket.send({ type: 'round.requestState', roundId });
      setHoleEvents((prev) => [
        ...prev,
        {
          playerId: winnerId,
          holeNumber: entry.holeNumber,
          eventType: 'mysteryWinner',
          eventValue: subgameId,
          occurredAt: timestamp,
        },
      ]);
    },
    [apiBaseUrl, mysteryEntriesForHole, roundId, socketClient, user?.token]
  );
  const displayTotals: DisplayTotal[] = useMemo(() => {
    const sourceTotals = leaderboardTotals.length > 0 ? leaderboardTotals : totals;
    const useGameTotals = Object.keys(gameResults).length > 0;
    const playerTotals = new Map<string, number>();
    const leaderboardPointTotals = new Map<string, number>();
    sourceTotals.forEach((total) => {
      const match = players.find((p) => p.id === total.playerId || p.name === total.playerName);
      const key = total.playerId ?? match?.id ?? total.playerName ?? '';
      const scoreName = match?.name ?? total.playerName ?? total.playerId ?? '';
      if (useGameTotals) {
        let sum = 0;
        Object.values(gameResults).forEach((game) => {
          const idKey = normalizePlayerKey(key);
          const nameKey = normalizePlayerKey(scoreName);
          Object.entries(game.scorePoints).forEach(([k, v]) => {
            const norm = normalizePlayerKey(k);
            if (norm === idKey || norm === nameKey) {
              sum += v;
            }
          });
        });
        if (key) playerTotals.set(key, sum);
        if (scoreName) playerTotals.set(scoreName, sum);
      }
    });
    Object.entries(leaderboardGamePoints).forEach(([playerKey, points]) => {
      const totalPoints = points.reduce((sum, gp) => sum + (gp.roundGamePoints ?? 0), 0);
      leaderboardPointTotals.set(playerKey, totalPoints);
    });

    const resolveRoundPoints = (key: string, name: string) =>
      leaderboardPointTotals.get(key) ??
      leaderboardPointTotals.get(name) ??
      playerTotals.get(key) ??
      playerTotals.get(name) ??
      0;
    const sorted = [...sourceTotals].sort((a, b) => {
      const aKey = a.playerId ?? a.playerName ?? '';
      const bKey = b.playerId ?? b.playerName ?? '';
      const aScoreName = a.playerName ?? a.playerId ?? '';
      const bScoreName = b.playerName ?? b.playerId ?? '';
      const aScore = resolveRoundPoints(aKey, aScoreName);
      const bScore = resolveRoundPoints(bKey, bScoreName);
      return bScore - aScore;
    });
    const leaderScores = new Map<string, number>();
    sorted.forEach((total) => {
      const key = total.playerId ?? total.playerName ?? '';
      const scoreName = total.playerName ?? total.playerId ?? '';
      leaderScores.set(key, resolveRoundPoints(key, scoreName));
    });
    const scoreValues = Array.from(leaderScores.values());
    const leaderScore = scoreValues.length ? Math.max(...scoreValues) : 0;
    const hasLeaderData = scoreValues.some((score) => score !== 0);
    const leaderKeys = new Set(
      Array.from(leaderScores.entries())
        .filter(([, score]) => score === leaderScore)
        .map(([key]) => key)
    );
    const distinctScoreCount = new Set(leaderScores.values()).size;
    const hasDistinctLeader = distinctScoreCount > 1 && leaderKeys.size > 0;
    const roundRankByIdentity = new Map<string, number>();
    if (hasLeaderData && hasDistinctLeader) {
      let previousScore: number | null = null;
      let currentRank = 0;
      sorted.forEach((item, index) => {
        const itemKey = item.playerId ?? item.playerName ?? '';
        const itemScoreName = item.playerName ?? item.playerId ?? '';
        const itemScore = resolveRoundPoints(itemKey, itemScoreName);
        if (previousScore === null || itemScore !== previousScore) {
          currentRank = index + 1;
          previousScore = itemScore;
        }
        roundRankByIdentity.set(itemKey, currentRank);
        roundRankByIdentity.set(itemScoreName, currentRank);
      });
    }

    return sorted.map((total) => {
      const match = players.find((p) => p.id === total.playerId || p.name === total.playerName);
      const key = total.playerId ?? match?.id ?? total.playerName;
      const scoreName = match?.name ?? total.playerName ?? total.playerId ?? '';
      const candidateName = match?.name ?? total.playerName ?? '';
      const displayName =
        candidateName && !isUuidLike(candidateName)
          ? candidateName
          : scoreName && !isUuidLike(scoreName)
            ? scoreName
            : t('hole.unknownPlayer');
      const roundPoints = useGameTotals
        ? resolveRoundPoints(key ?? '', scoreName)
        : undefined;
      const tourPoints = tourTotals[key ?? ''] ?? tourTotals[scoreName];
      const isLeader = hasLeaderData && hasDistinctLeader && (leaderKeys.has(key ?? '') || leaderKeys.has(scoreName ?? ''));
      const rank = roundRankByIdentity.get(key ?? '') ?? roundRankByIdentity.get(scoreName);
      return {
        key: key ?? scoreName,
        name: displayName,
        totalStrokes: tourPoints ?? 0,
        roundPoints,
        pickups: total.pickups ?? 0,
        isLeader,
        roundRank: rank,
      };
    });
  }, [gameResults, leaderboardGamePoints, leaderboardTotals, players, totals, tourTotals]);
  const tourLeaderKeys = useMemo(() => {
    let maxPoints = -Infinity;
    displayTotals.forEach((total) => {
      if (typeof total.totalStrokes !== 'number') return;
      if (total.totalStrokes > maxPoints) maxPoints = total.totalStrokes;
    });
    if (!Number.isFinite(maxPoints) || maxPoints <= 0) return new Set<string>();
    const keys = new Set<string>();
    displayTotals.forEach((total) => {
      if (total.totalStrokes === maxPoints) {
        keys.add(normalizePlayerKey(total.key));
        keys.add(normalizePlayerKey(total.name));
      }
    });
    return keys;
  }, [displayTotals]);
  const tourPlacementByKey = useMemo(() => {
    const map: Record<string, number> = {};
    const ranked = [...displayTotals]
      .filter((total) => typeof total.totalStrokes === 'number')
      .sort((a, b) => b.totalStrokes - a.totalStrokes);
    let previousPoints: number | null = null;
    let currentRank = 0;
    ranked.forEach((total, index) => {
      if (previousPoints === null || total.totalStrokes !== previousPoints) {
        currentRank = index + 1;
        previousPoints = total.totalStrokes;
      }
      if (currentRank > 3) return;
      const keyNorm = normalizePlayerKey(total.key);
      const nameNorm = normalizePlayerKey(total.name);
      map[keyNorm] = currentRank;
      map[nameNorm] = currentRank;
    });
    return map;
  }, [displayTotals]);
  const roundPlacementByKey = useMemo(() => {
    const map: Record<string, number> = {};
    displayTotals.forEach((total) => {
      if (!total.roundRank || total.roundRank > 3) return;
      const keyNorm = normalizePlayerKey(total.key);
      const nameNorm = normalizePlayerKey(total.name);
      map[keyNorm] = total.roundRank;
      map[nameNorm] = total.roundRank;
    });
    return map;
  }, [displayTotals]);
  const renderPlacementBadge = useCallback((roundPlace?: number, tourPlace?: number) => {
    const hasRound = !!roundPlace && roundPlace >= 1 && roundPlace <= 3;
    const hasTour = !!tourPlace && tourPlace >= 1 && tourPlace <= 3;
    if (!hasRound && !hasTour) return null;
    const medal = hasRound ? (roundPlace === 1 ? '🥇' : roundPlace === 2 ? '🥈' : '🥉') : null;
    const cupPlace = hasTour ? tourPlace : null;
    return (
      <View style={styles.roundPlacementBadge}>
        {medal ? <Text style={styles.roundPlacementMedal}>{medal}</Text> : null}
        {cupPlace ? (
          <Image
            source={TrophyOnlyImage}
            style={[
              styles.roundPlacementCupIcon,
              cupPlace === 2 ? styles.roundPlacementCupIconSilver : null,
              cupPlace === 3 ? styles.roundPlacementCupIconBronze : null,
            ]}
            resizeMode="contain"
          />
        ) : null}
      </View>
    );
  }, []);
  const orderedTeams = useMemo(() => {
    if (!teams.length) return [];
    return [...teams].sort((a, b) => {
      const aKey = (a.name ?? a.id).toLowerCase();
      const bKey = (b.name ?? b.id).toLowerCase();
      return aKey.localeCompare(bKey);
    });
  }, [teams]);
  const gameResultPlayers = useMemo(() => {
    if (players.length > 0) {
      if (!orderedTeams.length) return players;
      const playerById = new Map(players.map((p) => [p.id, p]));
      const ordered: Player[] = [];
      orderedTeams.forEach((team) => {
        (team.players ?? []).forEach((pid) => {
          const player = playerById.get(pid);
          if (player) ordered.push(player);
        });
      });
      const remaining = players.filter((p) => !ordered.some((op) => op.id === p.id));
      return [...ordered, ...remaining];
    }
    // Derive lightweight player list from gameResults keys when player list is missing.
    const keys = new Set<string>();
    Object.values(gameResults).forEach((game) => {
      Object.keys(game.scorePoints).forEach((pid) => keys.add(pid));
    });
    return Array.from(keys).map((k) => ({ id: k, name: k, createdAt: '' }));
  }, [players, gameResults, orderedTeams]);
  const gameDefinitionNameById = useMemo(() => {
    const map: Record<string, string> = {};
    gameDefinitionNames.forEach((def) => {
      if (!def.id) return;
      const name = getGameDisplayName(
        {
          id: def.id,
          name: def.name ?? (def as { Name?: string }).Name,
          info: def.info ?? (def as { Info?: string }).Info,
          description: def.description ?? (def as { Description?: string }).Description,
        },
        t('hole.gameFallbackName')
      );
      if (name) {
        map[def.id] = name;
      }
    });
    return map;
  }, [gameDefinitionNames]);
  const gameResultRows = useMemo(() => {
    const selectedGameIds = new Set(selectedGameDefinitionIds);
    const teamIdByPlayerKey = new Map<string, string>();
    orderedTeams.forEach((team) => {
      (team.players ?? []).forEach((pid) => {
        teamIdByPlayerKey.set(normalizePlayerKey(pid), team.id);
      });
    });
    players.forEach((player) => {
      const teamId = teamIdByPlayerKey.get(normalizePlayerKey(player.id));
      if (teamId) {
        teamIdByPlayerKey.set(normalizePlayerKey(player.name), teamId);
      }
    });
    const gameIds = new Set<string>();
    Object.keys(gameResults).forEach((id) => {
      if (selectedGameIds.size > 0 && !selectedGameIds.has(id)) return;
      gameIds.add(id);
    });
    Object.values(leaderboardGamePoints).forEach((points) => {
      points.forEach((gp) => {
        if (gp.gameId) {
          if (selectedGameIds.size > 0 && !selectedGameIds.has(gp.gameId)) return;
          gameIds.add(gp.gameId);
        }
      });
    });
    const leaderboardPointsByPlayer = new Map<string, Record<string, number>>();
    Object.entries(leaderboardGamePoints).forEach(([playerKey, points]) => {
      const pointMap: Record<string, number> = {};
      points.forEach((gp) => {
        if (!gp.gameId) return;
        pointMap[gp.gameId] = gp.roundGamePoints;
      });
      leaderboardPointsByPlayer.set(normalizePlayerKey(playerKey), pointMap);
    });

    return Array.from(gameIds)
      .map((gameId) => {
      const game = gameResults[gameId];
      const gameName = getGameDisplayName(
        { id: gameId, name: game?.name ?? gameDefinitionNameById[gameId] },
        t('hole.gameFallbackName')
      );
      if (isHazardGameName(gameName)) {
        return null;
      }
      const scores = gameResultPlayers.map((p) => {
        const idKey = normalizePlayerKey(p.id);
        const nameKey = normalizePlayerKey(p.name);
        let score = 0;
        if (game?.scorePoints) {
          Object.entries(game.scorePoints).forEach(([k, v]) => {
            const norm = normalizePlayerKey(k);
            if (norm === idKey || norm === nameKey) {
              score = v;
            }
          });
        } else {
          const idPoints = leaderboardPointsByPlayer.get(idKey);
          const namePoints = leaderboardPointsByPlayer.get(nameKey);
          score = idPoints?.[gameId] ?? namePoints?.[gameId] ?? 0;
        }
        return score;
      });
      const maxScore = scores.length > 0 ? Math.max(...scores) : 0;
      const leaderFlags = scores.map((s) => s === maxScore && maxScore > 0);
      const isTeamGame = gameDefinitionTypes[gameId] === 'team';
      let sharedLeaders = leaderFlags.map(() => false);
      if (maxScore > 0) {
        if (isTeamGame && orderedTeams.length > 0) {
          const leaderTeamIds = new Set<string>();
          leaderFlags.forEach((isLeader, idx) => {
            if (!isLeader) return;
            const player = gameResultPlayers[idx];
            const teamId =
              teamIdByPlayerKey.get(normalizePlayerKey(player?.id)) ??
              teamIdByPlayerKey.get(normalizePlayerKey(player?.name ?? ''));
            if (teamId) {
              leaderTeamIds.add(teamId);
            }
          });
          const shared = leaderTeamIds.size > 1;
          if (shared) {
            sharedLeaders = leaderFlags.map((isLeader) => isLeader);
          }
        } else {
          const shared = leaderFlags.filter(Boolean).length > 1;
          if (shared) {
            sharedLeaders = leaderFlags.map((isLeader) => isLeader);
          }
        }
      }

      return {
        key: gameId,
        name: gameName,
        scores,
        leaders: leaderFlags,
        sharedLeaders,
      };
    })
      .filter((row): row is { key: string; name: string; scores: number[]; leaders: boolean[]; sharedLeaders: boolean[] } => row !== null);
  }, [
    gameDefinitionNameById,
    gameDefinitionTypes,
    gameResults,
    gameResultPlayers,
    leaderboardGamePoints,
    orderedTeams,
    players,
    selectedGameDefinitionIds,
  ]);
  const hazardIconsByPlayer = useMemo(() => {
    const playerIdByName = new Map(players.map((p) => [normalizePlayerKey(p.name), p.id]));
    const result = new Map<string, Array<typeof WaterIcon>>();
    const typesByPlayer = new Map<string, Set<string>>();
    Object.entries(gameResults).forEach(([gameId, game]) => {
      const hazardType = hazardTypeFromName(game.name);
      if (!hazardType) return;
      if (!selectedHazardTypes.has(hazardType)) return;
      const icon = hazardIconForGameName(game.name);
      if (!icon) return;
      Object.entries(game.scorePoints).forEach(([playerKey, points]) => {
        if (points >= 0) return;
        const normalizedKey = normalizePlayerKey(playerKey);
        const resolvedId = playerIdByName.get(normalizedKey) ?? playerKey;
        const list = result.get(resolvedId) ?? [];
        if (!list.includes(icon)) {
          list.push(icon);
          result.set(resolvedId, list);
        }
        const types = typesByPlayer.get(resolvedId) ?? new Set<string>();
        types.add(hazardType);
        typesByPlayer.set(resolvedId, types);
      });
    });

    return {
      getFor: (key: string, name: string) => {
        const direct = result.get(key);
        if (direct) return direct;
        const id = playerIdByName.get(normalizePlayerKey(name));
        return id ? result.get(id) ?? [] : [];
      },
      getTypesFor: (key: string, name: string) => {
        const direct = typesByPlayer.get(key);
        if (direct) return direct;
        const id = playerIdByName.get(normalizePlayerKey(name));
        return id ? typesByPlayer.get(id) ?? new Set<string>() : new Set<string>();
      },
    };
  }, [gameResults, players, selectedHazardTypes]);
  const compactHazardColumns = useMemo(() => {
    const order = ['water', 'penalty', 'bunker', 'threeputt'];
    return order
      .filter((type) => selectedHazardTypes.has(type))
      .map((type) => ({ type, icon: hazardIconForType(type) }))
      .filter((entry) => entry.icon);
  }, [selectedHazardTypes]);
  const selectedPlayer = useMemo(() => {
    if (!selectedPlayerId) return null;
    const match = players.find((p) => p.id === selectedPlayerId) ?? displayTotals.find((t) => t.key === selectedPlayerId);
    if (!match) return { id: selectedPlayerId, name: selectedPlayerId };
    if ('name' in match) return { id: selectedPlayerId, name: match.name };
    return { id: selectedPlayerId, name: selectedPlayerId };
  }, [displayTotals, players, selectedPlayerId]);
  const selectedPlayerGamePoints = useMemo(() => {
    if (!selectedPlayer) return [];
    const selectedGameIds = new Set(selectedGameDefinitionIds);
    const leaderboardPoints =
      leaderboardGamePoints[selectedPlayer.id] ?? leaderboardGamePoints[selectedPlayer.name] ?? [];
    const idKey = normalizePlayerKey(selectedPlayer.id);
    const nameKey = normalizePlayerKey(selectedPlayer.name);
    const baseRows = Object.entries(gameResults).map(([gameId, game]) => {
      if (selectedGameIds.size > 0 && !selectedGameIds.has(gameId)) {
        return null;
      }
      let score = 0;
      Object.entries(game.scorePoints).forEach(([k, v]) => {
        const norm = normalizePlayerKey(k);
        if (norm === idKey || norm === nameKey) {
          score = v;
        }
      });
      return { id: gameId, name: getGameDisplayName(game, t('hole.gameFallbackName')), points: score };
    });
    const filteredBaseRows = baseRows.filter(
      (row): row is { id: string; name: string; points: number; position?: number } => row !== null
    );
    if (leaderboardPoints.length === 0) {
      return filteredBaseRows;
    }

    const pointsByGame = new Map(
      leaderboardPoints.map((gp) => [
        gp.gameId ?? 'game',
        { points: gp.roundGamePoints, position: gp.position },
      ])
    );
    const merged = filteredBaseRows.map((row) => {
      const override = pointsByGame.get(row.id);
      if (!override) return row;
      return { ...row, points: override.points, position: override.position };
    });
    leaderboardPoints.forEach((gp) => {
      const gameId = gp.gameId ?? 'game';
      if (selectedGameIds.size > 0 && !selectedGameIds.has(gameId)) return;
      if (merged.some((row) => row.id === gameId)) return;
      merged.push({
        id: gameId,
        name: getGameDisplayName(gameResults[gameId], t('hole.gameFallbackName')),
        points: gp.roundGamePoints,
        position: gp.position,
      });
    });
    return merged;
  }, [gameResults, leaderboardGamePoints, selectedGameDefinitionIds, selectedPlayer]);
  const gamePositionsByPlayer = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    const positionSeed: Record<string, Record<string, number>> = {};
    leaderboardTotals.forEach((total) => {
      const playerId = total.playerId;
      if (!playerId || !total.roundGamePoints) return;
      total.roundGamePoints.forEach((gp) => {
        const gameId = gp.gameId ?? 'game';
        map[gameId] ??= {};
        map[gameId][playerId] = gp.roundGamePoints;
        if (gp.position !== undefined) {
          positionSeed[gameId] ??= {};
          positionSeed[gameId][playerId] = gp.position;
        }
      });
    });
    const positionMap: Record<string, Record<string, number>> = {};
    Object.entries(map).forEach(([gameId, pointsByPlayer]) => {
      const isTeamGame = gameDefinitionTypes[gameId] === 'team';
      if (isTeamGame && orderedTeams.length > 0) {
        const teamEntries = orderedTeams
          .map((team) => {
            const members = team.players ?? [];
            const memberPoints = members
              .map((pid) => pointsByPlayer[pid])
              .filter((value): value is number => typeof value === 'number');
            if (memberPoints.length === 0) return null;
            return { teamId: team.id, points: Math.max(...memberPoints), players: members };
          })
          .filter((entry): entry is { teamId: string; points: number; players: string[] } => entry !== null);
        teamEntries.sort((a, b) => b.points - a.points);
        let positionIndex = 1;
        for (let i = 0; i < teamEntries.length; ) {
          const currentPoints = teamEntries[i].points;
          const tieGroup = teamEntries.slice(i).filter((e) => e.points === currentPoints);
          tieGroup.forEach((entry) => {
            entry.players.forEach((playerId) => {
              positionMap[gameId] ??= {};
              positionMap[gameId][playerId] = positionIndex;
            });
          });
          positionIndex += tieGroup.length;
          i += tieGroup.length;
        }
        return;
      }
      if (!isTeamGame && positionSeed[gameId]) {
        positionMap[gameId] = { ...positionSeed[gameId] };
        return;
      }
      const entries = Object.entries(pointsByPlayer).map(([playerId, points]) => ({ playerId, points }));
      entries.sort((a, b) => b.points - a.points);
      let positionIndex = 1;
      for (let i = 0; i < entries.length; ) {
        const currentPoints = entries[i].points;
        const tieGroup = entries.slice(i).filter((e) => e.points === currentPoints);
        tieGroup.forEach((entry) => {
          positionMap[gameId] ??= {};
          positionMap[gameId][entry.playerId] = positionIndex;
        });
        positionIndex += tieGroup.length;
        i += tieGroup.length;
      }
    });
    return positionMap;
  }, [gameDefinitionTypes, leaderboardTotals, orderedTeams]);
  const positionShareByGame = useMemo(() => {
    const sharedByGame: Record<string, Record<string, boolean>> = {};
    const pointsByGame: Record<string, Record<string, number>> = {};
    leaderboardTotals.forEach((total) => {
      const playerId = total.playerId;
      if (!playerId || !total.roundGamePoints) return;
      total.roundGamePoints.forEach((gp) => {
        const gameId = gp.gameId ?? 'game';
        pointsByGame[gameId] ??= {};
        pointsByGame[gameId][playerId] = gp.roundGamePoints;
      });
    });

    Object.entries(pointsByGame).forEach(([gameId, pointsByPlayer]) => {
      const isTeamGame = gameDefinitionTypes[gameId] === 'team';
      if (isTeamGame && orderedTeams.length > 0) {
        const teamEntries = orderedTeams
          .map((team) => {
            const members = team.players ?? [];
            const memberPoints = members
              .map((pid) => pointsByPlayer[pid])
              .filter((value): value is number => typeof value === 'number');
            if (memberPoints.length === 0) return null;
            return { teamId: team.id, points: Math.max(...memberPoints), players: members };
          })
          .filter((entry): entry is { teamId: string; points: number; players: string[] } => entry !== null);
        teamEntries.sort((a, b) => b.points - a.points);
        let positionIndex = 1;
        for (let i = 0; i < teamEntries.length; ) {
          const currentPoints = teamEntries[i].points;
          const tieGroup = teamEntries.slice(i).filter((e) => e.points === currentPoints);
          const isShared = tieGroup.length > 1;
          if (isShared) {
            tieGroup.forEach((entry) => {
              entry.players.forEach((playerId) => {
                sharedByGame[gameId] ??= {};
                sharedByGame[gameId][playerId] = true;
              });
            });
          }
          positionIndex += tieGroup.length;
          i += tieGroup.length;
        }
        return;
      }

      const positionCounts: Record<number, number> = {};
      Object.entries(pointsByPlayer).forEach(([playerId, points]) => {
        const pos = gamePositionsByPlayer[gameId]?.[playerId];
        if (!pos) return;
        positionCounts[pos] = (positionCounts[pos] ?? 0) + 1;
      });
      Object.entries(pointsByPlayer).forEach(([playerId]) => {
        const pos = gamePositionsByPlayer[gameId]?.[playerId];
        if (!pos) return;
        if ((positionCounts[pos] ?? 0) > 1) {
          sharedByGame[gameId] ??= {};
          sharedByGame[gameId][playerId] = true;
        }
      });
    });

    return sharedByGame;
  }, [gameDefinitionTypes, gamePositionsByPlayer, leaderboardTotals, orderedTeams]);
  const defaultTeamColor = '#d5e1ff';
  const defaultScoreColor = '#34d399';
  const hasSelectedTeamGame = useMemo(
    () =>
      selectedGameDefinitionIds.some(
        (gameId) => (gameDefinitionTypes[gameId] ?? '').toLowerCase() === 'team'
      ),
    [gameDefinitionTypes, selectedGameDefinitionIds]
  );
  const hasActiveTeamPlay = hasSelectedTeamGame && orderedTeams.length > 0;
  const teamColors = useMemo(() => {
    const palette = ['#f97316', '#06b6d4', '#a855f7', '#22c55e', '#eab308', '#f43f5e'];
    const map: Record<string, string> = {};
    if (orderedTeams.length > 0) {
      orderedTeams.forEach((team, idx) => {
        const color = palette[idx % palette.length];
        (team.players ?? []).forEach((pid) => {
          map[pid] = color;
        });
      });
      return map;
    }
    gameResultPlayers.forEach((p, idx) => {
      const teamIdx = Math.floor(idx / 2);
      map[p.id] = palette[teamIdx % palette.length];
    });
    return map;
  }, [gameResultPlayers, orderedTeams]);
  const teamColorByKey = useMemo(() => {
    const map: Record<string, string> = { ...teamColors };
    players.forEach((player) => {
      const color = teamColors[player.id];
      if (!color) return;
      map[player.name] = color;
      map[normalizePlayerKey(player.name)] = color;
      map[normalizePlayerKey(player.id)] = color;
    });
    return map;
  }, [players, teamColors]);
  const teamLabelByPlayer = useMemo(() => {
    const map: Record<string, string> = {};
    orderedTeams.forEach((team) => {
      const label = team.name ?? team.id;
      (team.players ?? []).forEach((pid) => {
        map[pid] = label;
      });
    });
    players.forEach((player) => {
      const label = map[player.id];
      if (label) {
        map[player.name] = label;
        map[normalizePlayerKey(player.name)] = label;
        map[normalizePlayerKey(player.id)] = label;
      }
    });
    return map;
  }, [orderedTeams, players]);
  const resolveTeamLabel = useCallback(
    (key?: string) => {
      if (!key) return null;
      return teamLabelByPlayer[normalizePlayerKey(key)] ?? teamLabelByPlayer[key] ?? null;
    },
    [teamLabelByPlayer]
  );
  const resolveTeamColor = useCallback(
    (key?: string) => {
      if (!hasActiveTeamPlay) return defaultTeamColor;
      if (!key) return defaultTeamColor;
      return teamColorByKey[normalizePlayerKey(key)] ?? teamColorByKey[key] ?? defaultTeamColor;
    },
    [defaultTeamColor, hasActiveTeamPlay, teamColorByKey]
  );
  const leaderboardPanel = !hasSelectedGames ? (
    <View style={styles.scoreboard} lightColor="#0f1c3c" darkColor="#0f1c3c">
      <Text style={styles.scoreboardTitle}>{t('hole.leaderboardRoundTitle')}</Text>
      <Text style={styles.scoreboardEmpty}>{t('hole.noGamesSelected')}</Text>
    </View>
  ) : displayTotals.length > 0 ? (
    <View style={styles.scoreboard} lightColor="#0f1c3c" darkColor="#0f1c3c">
      <Text style={styles.scoreboardTitle}>{t('hole.leaderboardRoundTitle')}</Text>
      {displayTotals.map((total) => (
        <Pressable
          key={total.key}
          style={styles.scoreboardRow}
          onPress={() => setSelectedPlayerId(total.key)}
        >
          {(() => {
            const placement =
              tourPlacementByKey[normalizePlayerKey(total.key)] ??
              tourPlacementByKey[normalizePlayerKey(total.name)];
            const roundPlacement =
              roundPlacementByKey[normalizePlayerKey(total.key)] ??
              roundPlacementByKey[normalizePlayerKey(total.name)];
            return (
          <View style={styles.scoreboardNameRow}>
            <View style={styles.scoreboardNameContent}>
              {renderPlacementBadge(roundPlacement, placement)}
              {mysteryWinnerIdsForHole.has(total.key) ? (
                <Image source={MysteryPrizeIcon} style={styles.mysteryWinnerIcon} resizeMode="contain" />
              ) : null}
              {hazardIconsByPlayer.getFor(total.key, total.name).map((icon, idx) => (
                <Image key={`${total.key}-hazard-${idx}`} source={icon} style={styles.hazardIcon} resizeMode="contain" />
              ))}
              <Text style={[styles.scoreboardName, { color: resolveTeamColor(total.key ?? total.name) }]}>
                {`${(() => {
                  const placement =
                    tourPlacementByKey[normalizePlayerKey(total.key)] ??
                    tourPlacementByKey[normalizePlayerKey(total.name)];
                  const isTourLeader =
                    tourLeaderKeys.has(normalizePlayerKey(total.key)) || tourLeaderKeys.has(normalizePlayerKey(total.name));
                  return isTourLeader && !placement ? '🏆 ' : '';
                })()}${total.name}`}
                {(() => {
                  const teamLabel = resolveTeamLabel(total.key ?? total.name);
                  return teamLabel ? ` (${teamLabel})` : '';
                })()}
              </Text>
            </View>
            <View style={styles.infoIconSmall}>
              <Text style={styles.infoIconSmallText}>i</Text>
            </View>
          </View>
            );
          })()}
          <Text style={[styles.scoreboardValue, { color: resolveTeamColor(total.key ?? total.name) }]}>
            {total.roundPoints !== undefined
              ? ` ${total.roundPoints}`
              : ` ${t('hole.roundPointsMissing')}`}
            {` (${total.totalStrokes})`}
            <Text
              style={styles.scoreboardValueDetails}
              onPress={() => setSelectedPlayerId(total.key)}
              accessibilityRole="button"
            >

            </Text>
          </Text>
        </Pressable>
      ))}
    </View>
  ) : null;
  const gameResultsPanel = hasSelectedGames && Object.keys(gameResults).length > 0 ? (
    <View style={styles.scoreboard} lightColor="#0f1c3c" darkColor="#0f1c3c">
      <View style={styles.gameResultsFrame}>
        <View style={[styles.gameResultsRow, styles.gameResultsHeader]}>
          <Text style={[styles.scoreboardTitle, styles.gameResultsName]}>{t('hole.gamesTitle')}</Text>
          {gameResultPlayers.map((p) => (
            <Text
              key={p.id}
              style={[styles.gameResultsPlayer, { color: hasActiveTeamPlay ? (teamColors[p.id] ?? defaultTeamColor) : defaultTeamColor }]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {compactPlayerName(p.name)}
            </Text>
          ))}
        </View>
        {gameResultRows.map((row) => (
          <View key={row.key} style={styles.gameResultsRow}>
            <Text style={styles.gameResultsName} numberOfLines={1} ellipsizeMode="tail">
              {row.name}
            </Text>
            {row.scores.map((score, idx) => (
              <View key={`${row.key}-${idx}`} style={styles.gameResultCell}>
                <View
                  style={[
                    row.leaders[idx] ? styles.gameResultLeader : styles.gameResultValueWrapper,
                    row.sharedLeaders?.[idx] ? styles.gameResultLeaderShared : null,
                  ]}
                >
                  <Text
                    style={[
                      styles.gameResultValue,
                      {
                        color: hasActiveTeamPlay
                          ? (teamColors[gameResultPlayers[idx]?.id ?? ''] ?? defaultScoreColor)
                          : defaultScoreColor,
                      },
                    ]}
                  >
                    {score}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        ))}
      </View>
    </View>
  ) : null;
  const topPanels = (
    <>
      {leaderboardPanel}
      {gameResultsPanel}
    </>
  );
  const pageHeight = isSwipeMode ? windowHeight : Math.max(windowHeight, 640);
  const holeHeader = (
    <ImageBackground source={HoleIllustration} style={styles.holeInfoBlock} imageStyle={styles.holeInfoImage} resizeMode="cover">
      <View style={styles.titleRow}>
        <View style={styles.holeTitleRow}>
          <Text style={styles.holeTitle}>{`${t('hole.title')} ${holeNumber}`}</Text>
        </View>
        <View style={styles.holeInfoRow}>
          <Text style={styles.holeInfoBadge}>{`Par ${parLabel}`}</Text>
          <Text style={styles.holeInfoBadge}>{`Hcp-index ${hcpLabel}`}</Text>
        </View>
      </View>
      {hasMysteryPrizeForHole ? (
        <Pressable style={styles.mysteryPrizeRow} onPress={() => setMysteryModalOpen(true)}>
          <Image source={MysteryPrizeIcon} style={styles.mysteryPrizeIcon} resizeMode="contain" />
          <Text style={styles.mysteryPrizeText}>{mysteryPrizeText}</Text>
        </Pressable>
      ) : null}
      {currentHoleMeta.name ? <Text style={styles.holeInfoName}>{currentHoleMeta.name}</Text> : null}
      {currentHoleMeta.description ? (
        <>
          {showHoleDetails ? <Text style={styles.holeInfoDescription}>{currentHoleMeta.description}</Text> : null}
          <Pressable style={styles.detailsToggle} onPress={() => setShowHoleDetails((prev) => !prev)}>
            <Text style={styles.detailsToggleText}>
              {showHoleDetails ? t('hole.hideDetails') : t('hole.showDetails')}
            </Text>
          </Pressable>
        </>
      ) : null}
    </ImageBackground>
  );
  const renderHoleHeaderSwipe = (player?: Player) => {
    const leaderKey = player?.id ?? player?.name ?? '';
    const isLeader = displayTotals.some((total) => total.isLeader && (total.key === leaderKey || total.name === leaderKey));
    const leaderPlacement =
      tourPlacementByKey[normalizePlayerKey(leaderKey)] ??
      (player?.name ? tourPlacementByKey[normalizePlayerKey(player.name)] : undefined);
    const roundPlacement =
      roundPlacementByKey[normalizePlayerKey(leaderKey)] ??
      (player?.name ? roundPlacementByKey[normalizePlayerKey(player.name)] : undefined);
    const entry =
      player && holeNumber
        ? holeEntries.find((h) => h.playerId === player.id && h.holeNumber === holeNumber)
        : undefined;
    const netScore = (() => {
      if (!player) return null;
      if (entry?.netStrokes !== null && entry?.netStrokes !== undefined) return entry.netStrokes;
      if (entry?.grossStrokes === null || entry?.grossStrokes === undefined) return null;
      if (strokeIndexForHole === null) return null;
      return computeNetForHole({
        gross: entry.grossStrokes,
        handicap: player.handicap ?? null,
        holeIndex: strokeIndexForHole,
        par: parForHole,
      }).netStrokes;
    })();
    const netScoreLabel = netScore !== null && netScore !== undefined ? netScore : '–';
    return (
      <View style={[styles.holeHeaderSwipe, isLeader && styles.holeHeaderSwipeLeader]}>
        <View style={styles.holeHeaderSwipeTitleRow}>
          {isLeader ? <Image source={TrophyOnlyImage} style={styles.leaderNameIcon} accessible={false} /> : null}
          {renderPlacementBadge(roundPlacement, leaderPlacement)}
          <Text style={[styles.holeHeaderSwipeTitle, { color: player ? resolveTeamColor(player.id) : undefined }]}>
            {(() => {
              const base = player?.name ? `${player.name} · ${t('hole.title')} ${holeNumber}` : `${t('hole.title')} ${holeNumber}`;
              if (!player || strokeIndexForHole === null) return base;
              const handicapStrokes =
                (holeEntries.find((h) => h.playerId === player.id)?.handicapStrokes ??
                  computeStrokesReceivedForHole(player.handicap ?? null, strokeIndexForHole)) ?? 0;
              return handicapStrokes > 0 ? `${base} (+${handicapStrokes})` : base;
            })()}
          </Text>
        </View>
        <Text style={styles.holeHeaderSwipeMeta}>
          {`Par ${parLabel} · Hcp-index ${hcpLabel} · ${t('hole.netScoreLabel')} ${netScoreLabel}`}
        </Text>
      </View>
    );
  };
  const renderPlayerCard = (player: Player) => {
    const isLeader =
      isSwipeMode &&
      displayTotals.some((total) => total.isLeader && (total.key === player.id || total.key === player.name));
    const existing = holeEntries.find((h) => {
      if (h.holeNumber !== holeNumber) return false;
      const playerName = player.name.trim().toLowerCase();
      const entryName = (h.playerName ?? '').trim().toLowerCase();
      return entryName === playerName || h.playerName === player.id;
    });
    const handicapStrokes =
      existing?.handicapStrokes ??
      computeStrokesReceivedForHole(player.handicap ?? null, strokeIndexForHole);
    const targetGross = parForHole && parForHole > 0 ? parForHole + handicapStrokes : null;
    const selectedFromExisting =
      existing && existing.grossStrokes !== null ? String(existing.grossStrokes) : existing ? 'Pickup' : undefined;
    const selected =
      scores[player.id] ??
      scores[player.name] ??
      selectedFromExisting;
    const netInfo =
      selected && selected !== 'Pickup'
        ? computeNetForHole({ gross: selected, handicap: player.handicap ?? null, holeIndex: strokeIndexForHole, par: parForHole })
        : null;
    const stablefordPoints =
      selected && selected !== 'Pickup' && parForHole
        ? Math.max(0, 2 + parForHole - parseInt(selected, 10))
        : null;
    const netHeaderStyle =
      netInfo && netInfo.netVsPar !== undefined
        ? netInfo.netVsPar < 0
          ? styles.netHintGain
          : netInfo.netVsPar > 0
            ? styles.netHintLoss
            : styles.netHintEven
        : styles.netHintEven;
    const eventKey = `${player.id}:${holeNumber}`;
    const counts = eventCounts[eventKey] ?? { water: 0, penalty: 0, bunker: 0, threePutt: 0 };
    const updateCounts = (field: keyof typeof counts, delta: number) => {
      let nextValue = 0;
      setEventCounts((prev) => {
        const next = { ...prev };
        const current = next[eventKey] ?? { water: 0, penalty: 0, bunker: 0, threePutt: 0 };
        nextValue = Math.max(0, current[field] + delta);
        next[eventKey] = { ...current, [field]: nextValue };
        return next;
      });
      setEventPopups((prev) => ({
        ...prev,
        [eventKey]: { label: t(`hole.event.${field}`), field },
      }));
      const existingTimeout = eventLabelTimeoutRef.current[eventKey];
      if (existingTimeout) clearTimeout(existingTimeout);
      eventLabelTimeoutRef.current[eventKey] = setTimeout(() => {
        setEventPopups((prev) => {
          const next = { ...prev };
          delete next[eventKey];
          return next;
        });
      }, 3000);
      if (socketClient && roundIdIsValid && roundId) {
        const sent = socketClient.send({
          type: 'hole.event',
          roundId,
          playerId: player.id,
          holeNumber,
          eventType: field,
          eventValue: String(nextValue),
          eventDelta: delta,
          timestamp: new Date().toISOString(),
        });
        if (!sent) {
          logWithContext(
            'hole.event.sendFailed',
            { message: t('hole.eventSendFailed'), roundId, hole: holeNumber, playerId: player.id, eventType: field },
            'warn'
          );
        }
      }
    };
    const eventPopup = eventPopups[eventKey];
    const eventIndex = eventPopup
      ? { water: 0, penalty: 1, bunker: 2, threePutt: 3 }[eventPopup.field]
      : 0;
    return (
      <View key={player.id} style={[styles.playerCard, isLeader && styles.playerCardLeader]}>
        {!isSwipeMode ? (
        <View style={styles.playerHeader}>
          <View style={styles.playerNameRow}>
            {mysteryWinnerIdsForHole.has(player.id) ? (
              <Image source={MysteryPrizeIcon} style={styles.mysteryWinnerIcon} resizeMode="contain" />
            ) : null}
            <Text
              style={[
                styles.playerName,
                isSwipeMode && styles.playerNameSwipe,
                { color: resolveTeamColor(player.id) },
              ]}
            >
              {player.name}
              {strokeIndexForHole !== null
                ? (() => {
                    const hcapStrokes =
                      (holeEntries.find((h) => h.playerId === player.id)?.handicapStrokes ??
                        computeStrokesReceivedForHole(player.handicap ?? null, strokeIndexForHole)) ?? 0;
                    return hcapStrokes > 0 ? `  +${hcapStrokes}` : '';
                  })()
                : ''}
            </Text>
          </View>
          <Text style={[styles.netSummary, netHeaderStyle]}>
            {netInfo
              ? t('hole.netSummary', { net: netInfo.netStrokes, vsPar: netInfo.netVsParLabel ?? '' })
              : t('hole.netSummaryEmpty')}
          </Text>
          {stablefordPoints !== null ? (
            <Text style={styles.stablefordSummary}>
              {t('hole.stablefordLabel')}: {stablefordPoints}
            </Text>
          ) : null}
        </View>
        ) : null}
        <View style={styles.scoreRowContainer}>
          {isSwipeMode ? (
            <View style={styles.scoreRowSwipe}>
              {[...Array(9)].map((_, idx) => String(idx + 1)).concat('10+').map((val) => {
                const showParDot = false;
                return (
                  <Pressable
                    key={val}
                    style={[
                      styles.scoreButton,
                      styles.scoreButtonSwipe,
                    ]}
                    onPress={() => handleScore(player, val)}
                    accessibilityRole="button"
                    accessibilityState={{ selected: selected === val }}
                  >
                    <Image
                      source={selected === val ? ScoreButtonSelectedImage : ScoreButtonImage}
                      style={[styles.scoreButtonImage, styles.scoreButtonImageSwipe]}
                      resizeMode="contain"
                      accessible={false}
                    />
                    <Text style={[styles.scoreButtonLabel, styles.scoreButtonLabelSwipe]}>{val}</Text>
                    {showParDot ? <View style={styles.netDot} /> : null}
                  </Pressable>
                );
              })}
              <Pressable
                style={[
                  styles.scoreButton,
                  styles.scoreButtonSwipe,
                ]}
                onPress={() => handleScore(player, 'Pickup')}
                accessibilityRole="button"
                accessibilityState={{ selected: selected === 'Pickup' }}
              >
                <Image
                  source={selected === 'Pickup' ? ScoreButtonSelectedImage : ScoreButtonImage}
                  style={[styles.scoreButtonImage, styles.scoreButtonImageSwipe]}
                  resizeMode="contain"
                  accessible={false}
                />
                <Text style={[styles.scoreButtonLabel, styles.scoreButtonLabelSwipe]}>Pickup</Text>
              </Pressable>
            </View>
          ) : (
            <ScrollView horizontal contentContainerStyle={styles.scoreRow} showsHorizontalScrollIndicator={false}>
              {[...Array(9)].map((_, idx) => {
                const val = String(idx + 1);
                const showParDot = false;
                return (
                  <Pressable
                    key={val}
                    style={[styles.scoreButton, selected === val && styles.scoreButtonSelected]}
                    onPress={() => handleScore(player, val)}
                    accessibilityRole="button"
                    accessibilityState={{ selected: selected === val }}
                  >
                    <Text style={[styles.scoreText, selected === val && styles.scoreTextSelected]}>{val}</Text>
                    {showParDot ? <View style={styles.netDot} /> : null}
                  </Pressable>
                );
              })}
              <Pressable
                style={[styles.scoreButton, selected === 'Pickup' && styles.scoreButtonSelected]}
                onPress={() => handleScore(player, 'Pickup')}
                accessibilityRole="button"
                accessibilityState={{ selected: selected === 'Pickup' }}
              >
                <Text style={[styles.scoreText, selected === 'Pickup' && styles.scoreTextSelected]}>Pickup</Text>
              </Pressable>
            </ScrollView>
          )}
        </View>
        {isSwipeMode ? (
          <View style={styles.puttRow}>
            {[
              { label: t('hole.puttOne'), count: 1 },
              { label: t('hole.puttTwo'), count: 2 },
              { label: t('hole.puttThree'), count: 3 },
            ].map(({ label, count }) => (
              <Pressable
                key={count}
                style={styles.puttButton}
                disabled
                accessibilityRole="button"
                accessibilityState={{ disabled: true }}
              >
                <Image
                  source={PuttButtonImage}
                  style={styles.puttButtonImage}
                  resizeMode="contain"
                  accessible={false}
                />
                <NativeView style={styles.puttButtonContent}>
                  {renderPuttLabel(label, count, t('hole.puttPlus'), {
                    puttBallRow: styles.puttBallRow,
                    puttBallIcon: styles.puttBallIcon,
                    puttText: styles.puttText,
                  })}
                </NativeView>
              </Pressable>
            ))}
          </View>
        ) : null}
        <View style={styles.eventRow}>
          {showWaterEvent ? (
            <Pressable
              style={[styles.eventItem, isSwipeMode && styles.eventItemSwipe]}
              onPress={() => updateCounts('water', 1)}
              onLongPress={() => updateCounts('water', -1)}
              accessibilityLabel={t('hole.event.water')}
            >
              <Image source={WaterIcon} style={[styles.eventImage, isSwipeMode && styles.eventImageSwipe]} resizeMode="contain" />
              <Text style={[styles.eventCount, isSwipeMode && styles.eventCountSwipe]}>{counts.water}</Text>
            </Pressable>
          ) : null}
          {showPenaltyEvent ? (
            <Pressable
              style={[styles.eventItem, isSwipeMode && styles.eventItemSwipe]}
              onPress={() => updateCounts('penalty', 1)}
              onLongPress={() => updateCounts('penalty', -1)}
              accessibilityLabel={t('hole.event.penalty')}
            >
              <Image source={PenaltyIcon} style={[styles.eventImage, isSwipeMode && styles.eventImageSwipe]} resizeMode="contain" />
              <Text style={[styles.eventCount, isSwipeMode && styles.eventCountSwipe]}>{counts.penalty}</Text>
            </Pressable>
          ) : null}
          {showBunkerEvent ? (
            <Pressable
              style={[styles.eventItem, isSwipeMode && styles.eventItemSwipe]}
              onPress={() => updateCounts('bunker', 1)}
              onLongPress={() => updateCounts('bunker', -1)}
              accessibilityLabel={t('hole.event.bunker')}
            >
              <Image source={BunkerIcon} style={[styles.eventImage, isSwipeMode && styles.eventImageSwipe]} resizeMode="contain" />
              <Text style={[styles.eventCount, isSwipeMode && styles.eventCountSwipe]}>{counts.bunker}</Text>
            </Pressable>
          ) : null}
          {showThreePuttEvent ? (
            <Pressable
              style={[styles.eventItem, isSwipeMode && styles.eventItemSwipe]}
              onPress={() => updateCounts('threePutt', 1)}
              onLongPress={() => updateCounts('threePutt', -1)}
              accessibilityLabel={t('hole.event.threePutt')}
            >
              <Image source={ThreePuttIcon} style={[styles.eventImage, isSwipeMode && styles.eventImageSwipe]} resizeMode="contain" />
              <Text style={[styles.eventCount, isSwipeMode && styles.eventCountSwipe]}>{counts.threePutt}</Text>
            </Pressable>
          ) : null}
          {eventPopup ? (
            <View style={[styles.eventBubble, { left: eventIndex * 52 }]}>
              <Text style={styles.eventBubbleText} numberOfLines={1}>
                {eventPopup.label}
              </Text>
            </View>
          ) : null}
        </View>
      </View>
    );
  };
  const swipeItems = useMemo(() => {
    if (!isSwipeMode) return [];
    if (players.length === 0) {
      return [
        { id: 'prev-hole', prevHole: true as const },
        { id: 'empty', empty: true as const },
        { id: 'summary', summary: true as const },
        { id: 'next-hole', nextHole: true as const },
      ];
    }
    return [
      { id: 'prev-hole', prevHole: true as const },
      ...players.map((player) => ({ id: player.id, player })),
      { id: 'summary', summary: true as const },
      { id: 'next-hole', nextHole: true as const },
    ];
  }, [isSwipeMode, players]);
  useEffect(() => {
    swipeItemsRef.current = swipeItems;
  }, [swipeItems]);
  const swipeOffsets = useMemo(() => {
    if (!isSwipeMode) return [];
    return Array.from({ length: swipeItems.length }, (_, idx) => idx * pageHeight);
  }, [isSwipeMode, pageHeight, swipeItems.length]);
  const initialSwipeIndex = useMemo(() => {
    if (!isSwipeMode || swipeItems.length === 0) return initialPageIndex;
    return Math.min(Math.max(initialPageIndex + 1, 0), swipeItems.length - 1);
  }, [initialPageIndex, isSwipeMode, swipeItems.length]);
  useEffect(() => {
    if (!isSwipeMode) return;
    if (!swipeItems.length || pageHeight <= 0) return;
    const clamped = Math.min(Math.max(initialSwipeIndex, 0), swipeItems.length - 1);
    activePageIndexRef.current = clamped;
    setActivePageIndex(clamped);
    swipeListRef.current?.scrollToIndex({ index: clamped, animated: false });
  }, [initialSwipeIndex, isSwipeMode, pageHeight, swipeItems.length]);
  useEffect(() => {
    activePageIndexRef.current = activePageIndex;
  }, [activePageIndex]);
  const renderSwipeItem = ({
    item,
  }: {
    item: SwipeItem;
    index: number;
  }) => (
    <View style={[styles.playerPage, { height: pageHeight }]}>
      {item.player && displayTotals.some((total) =>
        total.isLeader && (total.key === item.player.id || total.key === item.player.name)) ? (
        <Image
          source={TrophyOnlyImage}
          style={styles.playerPageLeaderBackground}
          resizeMode="contain"
          accessible={false}
        />
      ) : null}
      <View style={styles.playerPageContent}>
        {renderHoleHeaderSwipe(item.player)}
        {item.prevHole ? (
          <View style={styles.nextHoleCard} lightColor="#0f1c3c" darkColor="#0f1c3c">
            <Text style={styles.nextHoleTitle}>{t('hole.prevHole')}</Text>
            <Text style={styles.nextHoleHint}>{t('hole.prevHoleSwipeHint')}</Text>
            <Pressable
              style={[styles.nextHoleButton, holeNumber <= 1 && styles.nextHoleButtonDisabled]}
              onPress={() =>
                holeNumber > 1 &&
                navigateToHole(holeNumber - 1, { swipe: true, playerIndex: 0 })
              }
              disabled={holeNumber <= 1}
            >
              <Text style={[styles.nextHoleButtonText, holeNumber <= 1 && styles.nextHoleButtonTextDisabled]}>
                {t('hole.prevHole')}
              </Text>
            </Pressable>
          </View>
        ) : item.nextHole ? (
          (() => {
            const isLastHole = holeNumber >= holesCount;
            const nextLabel = isLastHole ? t('hole.finishRound') : t('hole.nextHole');
            const nextHint = isLastHole ? t('hole.finishRoundHint') : t('hole.nextHoleSwipeHint');
            return (
          <View style={styles.nextHoleCard} lightColor="#0f1c3c" darkColor="#0f1c3c">
            <Text style={styles.nextHoleTitle}>{nextLabel}</Text>
            <Text style={styles.nextHoleHint}>{nextHint}</Text>
            <Pressable
              style={[styles.nextHoleButton, styles.nextHoleButtonNext]}
              onPress={() =>
                isLastHole
                  ? finishRound()
                  : navigateToHole(holeNumber + 1, { swipe: true, playerIndex: 0 })
              }
            >
              <Text style={[styles.nextHoleButtonText, styles.nextHoleButtonTextOnGreen]}>{nextLabel}</Text>
            </Pressable>
          </View>
            );
          })()
        ) : item.summary ? (
          <>
            {!hasSelectedGames ? (
              <View style={styles.scoreboard} lightColor="#0f1c3c" darkColor="#0f1c3c">
                <Text style={styles.scoreboardTitle}>{t('hole.leaderboardRoundTitle')}</Text>
                <Text style={styles.scoreboardEmpty}>{t('hole.noGamesSelected')}</Text>
              </View>
            ) : displayTotals.length > 0 ? (
              <View style={styles.scoreboard} lightColor="#0f1c3c" darkColor="#0f1c3c">
                <Text style={styles.scoreboardTitle}>{t('hole.leaderboardRoundTitle')}</Text>
                {displayTotals.map((total) => (
                  <Pressable
                    key={total.key}
                    style={[styles.scoreboardRow, isSwipeMode && total.isLeader && styles.scoreboardRowLeader]}
                    onPress={() => setSelectedPlayerId(total.key)}
                  >
                    {isSwipeMode && total.isLeader ? (
                      <Image source={TrophyOnlyImage} style={styles.scoreboardLeaderBackground} resizeMode="contain" />
                    ) : null}
                    <View style={styles.scoreboardRowContent}>
                      <View style={styles.scoreboardNameRow}>
                        <View style={styles.scoreboardNameContent}>
                          {renderPlacementBadge(
                            roundPlacementByKey[normalizePlayerKey(total.key)] ??
                              roundPlacementByKey[normalizePlayerKey(total.name)],
                            tourPlacementByKey[normalizePlayerKey(total.key)] ??
                              tourPlacementByKey[normalizePlayerKey(total.name)]
                          )}
                          {isSwipeMode && total.isLeader ? (
                            <Image source={TrophyOnlyImage} style={styles.leaderNameIcon} />
                          ) : null}
                          {mysteryWinnerIdsForHole.has(total.key) ? (
                            <Image source={MysteryPrizeIcon} style={styles.mysteryWinnerIcon} resizeMode="contain" />
                          ) : null}
                          {hazardIconsByPlayer.getFor(total.key, total.name).map((icon, idx) => (
                            <Image key={`${total.key}-hazard-${idx}`} source={icon} style={styles.hazardIcon} resizeMode="contain" />
                          ))}
                          <Text style={[styles.scoreboardName, { color: resolveTeamColor(total.key ?? total.name) }]}>
                            {total.name}
                            {(() => {
                              const teamLabel = resolveTeamLabel(total.key ?? total.name);
                              return teamLabel ? ` (${teamLabel})` : '';
                            })()}
                          </Text>
                        </View>
                      </View>
                      <Text style={[styles.scoreboardValue, { color: resolveTeamColor(total.key ?? total.name) }]}>
                        {total.roundPoints !== undefined
                          ? ` ${total.roundPoints}`
                          : ` ${t('hole.roundPointsMissing')}`}
                        {` (${total.totalStrokes})`}
                      </Text>
                    </View>
                  </Pressable>
                ))}
              </View>
            ) : null}
            {hasSelectedGames && Object.keys(gameResults).length > 0 ? (
              <View style={styles.scoreboard} lightColor="#0f1c3c" darkColor="#0f1c3c">
                <View style={styles.gameResultsFrame}>
                  <View style={[styles.gameResultsRow, styles.gameResultsHeader]}>
                    <Text style={[styles.scoreboardTitle, styles.gameResultsName]}>{t('hole.gamesTitle')}</Text>
                    {gameResultPlayers.map((p) => (
                      <Text
                        key={p.id}
                        style={[styles.gameResultsPlayer, { color: hasActiveTeamPlay ? (teamColors[p.id] ?? defaultTeamColor) : defaultTeamColor }]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      >
                        {compactPlayerName(p.name)}
                      </Text>
                    ))}
                  </View>
                  {gameResultRows.map((row) => (
                    <View key={row.key} style={styles.gameResultsRow}>
                      <Text style={styles.gameResultsName} numberOfLines={1} ellipsizeMode="tail">
                        {row.name}
                      </Text>
                      {row.scores.map((score, idx) => (
                        <View key={`${row.key}-${idx}`} style={styles.gameResultCell}>
                          <Text style={styles.gameResultValue}>{score ?? '-'}</Text>
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              </View>
            ) : null}
          </>
        ) : (
          <>
            {item.empty ? <Text style={styles.subtitle}>{t('hole.noPlayers')}</Text> : null}
            {item.player ? renderPlayerCard(item.player) : null}
            {hasSelectedGames && gameResultRows.length > 0 ? (
              <View style={styles.leaderboardSwipe}>
                <View style={styles.gameMatrixHeader}>
                  <View style={styles.gameMatrixCorner} />
                  {gameResultPlayers.map((player) => (
                    <Text key={player.id} style={styles.gameMatrixPlayer} numberOfLines={1}>
                      {compactPlayerName(player.name)}
                    </Text>
                  ))}
                </View>
                {gameResultRows.map((row) => {
                  const icon = GAME_ICON_MAP[normalizeGameSlug(row.name)];
                  return (
                    <View key={row.key} style={styles.gameMatrixRow}>
                      <View style={styles.gameMatrixGame}>
                        {icon ? <Image source={icon} style={styles.gameColumnIcon} /> : null}
                        <Text style={styles.gameColumnTitle} numberOfLines={1}>
                          {row.name}
                        </Text>
                      </View>
                      {gameResultPlayers.map((player, idx) => (
                        <Text key={`${row.key}-${player.id}`} style={styles.gameMatrixScore}>
                          {row.scores[idx] ?? '-'}
                        </Text>
                      ))}
                    </View>
                  );
                })}
              </View>
            ) : null}
          </>
        )}
        <View style={styles.pageSpacer} />
        <View style={styles.holeNavRowSwipe}>
          <Pressable
            style={[styles.holeNavButtonSwipe, holeNumber <= 1 && styles.holeNavButtonDisabled]}
            onPress={() =>
              holeNumber > 1 &&
              navigateToHole(holeNumber - 1, {
                swipe: true,
                playerIndex: Math.max(activePageIndexRef.current - 1, 0),
              })
            }
            disabled={holeNumber <= 1}
          >
            <Text style={[styles.holeNavTextSwipe, holeNumber <= 1 && styles.holeNavTextDisabled]}>
              {t('hole.prevHole')}
            </Text>
          </Pressable>
          {holeNumber >= holesCount ? (
            <Pressable style={[styles.holeNavButtonSwipe, styles.holeNavButtonSwipeNext]} onPress={finishRound}>
              <Text style={[styles.holeNavTextSwipe, styles.holeNavTextOnGreen]}>{t('hole.finishRound')}</Text>
            </Pressable>
          ) : (
          <Pressable
            style={[styles.holeNavButtonSwipe, styles.holeNavButtonSwipeNext]}
            onPress={() =>
              navigateToHole(holeNumber + 1, {
                swipe: true,
                playerIndex: Math.max(activePageIndexRef.current - 1, 0),
              })
            }
          >
            <Text style={[styles.holeNavTextSwipe, styles.holeNavTextOnGreen]}>{t('hole.nextHole')}</Text>
          </Pressable>
          )}
        </View>
      </View>
    </View>
  );
  const isLastHole = holeNumber >= holesCount;

  const snapToNearestPage = useCallback(
    (offsetY: number) => {
      if (!swipeItems.length || pageHeight <= 0) return;
      const rawIndex = Math.round(offsetY / pageHeight);
      const index = Math.min(Math.max(rawIndex, 0), swipeItems.length - 1);
      swipeListRef.current?.scrollToIndex({ index, animated: true });
      activePageIndexRef.current = index;
      setActivePageIndex(index);
    },
    [pageHeight, swipeItems.length]
  );
  const viewabilityConfig = useRef({ itemVisiblePercentThreshold: 80 }).current;
  const onViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: Array<{ index?: number | null }> }) => {
      if (!viewableItems.length) return;
      const index = viewableItems[0]?.index ?? 0;
      const visibleItem = swipeItemsRef.current[index];
      activePageIndexRef.current = index;
      setActivePageIndex(index);
      if (visibleItem?.prevHole && !autoNavigatePrevHoleRef.current && holeNumberRef.current > 1) {
        autoNavigatePrevHoleRef.current = true;
        const nextHole = holeNumberRef.current - 1;
        setTimeout(() => {
          setHole(nextHole);
          if (!roundIdRef.current) return;
          const params: string[] = ['playerIndex=0'];
          if (debugRef.current) params.push('debug=true');
          const query = params.length ? `?${params.join('&')}` : '';
          router.push(`/round/${roundIdRef.current}/hole/${nextHole}/score${query}`);
        }, 0);
      } else if (visibleItem?.nextHole && !autoNavigateNextHoleRef.current) {
        autoNavigateNextHoleRef.current = true;
        if (holeNumberRef.current >= holesCountRef.current) {
          return;
        }
        const nextHole = holeNumberRef.current + 1;
        setTimeout(() => {
          setHole(nextHole);
          if (!roundIdRef.current) return;
          const params: string[] = ['playerIndex=0'];
          if (debugRef.current) params.push('debug=true');
          const query = params.length ? `?${params.join('&')}` : '';
          router.push(`/round/${roundIdRef.current}/hole/${nextHole}/score${query}`);
        }, 0);
      }
    }
  ).current;
  const holeSwipeResponder = useMemo(() => {
    if (!isSwipeMode) return null;
    const minSwipeDistance = 60;
    return PanResponder.create({
      onMoveShouldSetPanResponder: (_, gesture) => {
        const { dx, dy } = gesture;
        return Math.abs(dx) > Math.abs(dy) * 1.2 && Math.abs(dx) > 14;
      },
      onPanResponderRelease: (_, gesture) => {
        const { dx, dy } = gesture;
        if (Math.abs(dx) <= Math.abs(dy)) return;
        if (dx > minSwipeDistance && holeNumber > 1) {
          navigateToHole(holeNumber - 1, {
            swipe: true,
            playerIndex: Math.max(activePageIndexRef.current - 1, 0),
          });
        } else if (dx < -minSwipeDistance && !isLastHole) {
          navigateToHole(holeNumber + 1, {
            swipe: true,
            playerIndex: Math.max(activePageIndexRef.current - 1, 0),
          });
        }
      },
    });
  }, [holeNumber, isLastHole, isSwipeMode, navigateToHole]);


  if (!isNavigationReady) {
    return null;
  }

  if (Platform.OS === 'web' && !isClient) {
    return null;
  }

  if (!user) {
    return <Redirect href="/login" />;
  }

  if (!roundId) {
    return (
      <View style={styles.container}>
        <Text style={styles.subtitle}>{t('hole.missingRound')}</Text>
        <Pressable
          style={styles.primaryButton}
          onPress={() => {
            const debugSuffix = debug ? '?debug=true' : '';
            const nextPath = tourId ? `/tour/${tourId}/round-init${debugSuffix}` : '/tournaments';
            router.push(nextPath);
          }}
        >
          <Text style={styles.primaryButtonText}>{t('hole.goPlayers')}</Text>
        </Pressable>
      </View>
    );
  }

  if (!navState?.key) {
    return null;
  }

  if (isSwipeMode) {
    return (
      <View style={styles.swipeRoot} {...(holeSwipeResponder ? holeSwipeResponder.panHandlers : {})}>
        <FlatList
          ref={swipeListRef}
          data={swipeItems}
          renderItem={renderSwipeItem}
          keyExtractor={(item) => item.id}
          pagingEnabled
          snapToInterval={pageHeight}
          snapToOffsets={swipeOffsets}
          snapToAlignment="start"
          decelerationRate="fast"
          bounces={false}
          disableIntervalMomentum
          showsVerticalScrollIndicator={false}
          onMomentumScrollEnd={(event) => snapToNearestPage(event.nativeEvent.contentOffset.y)}
          onScrollEndDrag={(event) => snapToNearestPage(event.nativeEvent.contentOffset.y)}
          onViewableItemsChanged={onViewableItemsChanged}
          viewabilityConfig={viewabilityConfig}
          initialScrollIndex={initialSwipeIndex}
          getItemLayout={(_, index) => ({
            length: pageHeight,
            offset: pageHeight * index,
            index,
          })}
          contentContainerStyle={styles.swipeContent}
          style={styles.swipeList}
        />
        {celebrating ? (
          <View style={[styles.celebrateOverlay, { pointerEvents: 'box-none' }]}>
            {!showFinaleOnly ? (
              <Animated.Text style={[styles.bottle, { transform: [{ scale: bottleScale }] }]}>🍾</Animated.Text>
            ) : null}
            {showFinaleOnly ? (
              <Animated.Text style={[styles.finale, { transform: [{ scale: finaleScale }] }]}>🎆</Animated.Text>
            ) : null}
          </View>
        ) : null}
      <View style={[styles.birdieOverlay, { pointerEvents: 'none' }]}>
          <Animated.Image
            source={BirdieImage}
            style={[
              styles.birdieImage,
                {
                  opacity: birdieOpacity,
                  transform: [
                    { scale: birdieScale },
                    {
                      rotate: birdieRotate.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['0deg', '360deg'],
                      }),
                    },
                  ],
                },
            ]}
            resizeMode="contain"
          />
        </View>
        <Modal
          visible={nextMysteryModalOpen}
          transparent
          animationType="fade"
          onRequestClose={() => setNextMysteryModalOpen(false)}
        >
          <Pressable style={styles.modalBackdrop} onPress={() => setNextMysteryModalOpen(false)}>
            <Pressable style={styles.modalCard} onPress={() => null}>
              <Text style={styles.modalTitle}>{t('hole.mysteryPrizeNextHoleTitle')}</Text>
              <Text style={styles.modalSubtitle}>{t('hole.mysteryPrizeNextHoleSubtitle', { hole: holeNumber + 1 })}</Text>
              {nextMysteryModalRows.map((row, index) => (
                <View key={`${row.subgame}:${row.player}:${index}`} style={styles.modalRow}>
                  <Text style={styles.modalLabel}>{row.subgame}</Text>
                  <Text style={styles.modalValue}>{row.player}</Text>
                </View>
              ))}
              <Pressable style={styles.modalCloseButton} onPress={() => setNextMysteryModalOpen(false)}>
                <Text style={styles.modalCloseText}>{t('hole.close')}</Text>
              </Pressable>
            </Pressable>
          </Pressable>
        </Modal>
      </View>
    );
  }

  if (isCompactMode) {
    return (
      <View style={[styles.compactRoot, { minHeight: windowHeight }]}>
        <ScrollView
          contentContainerStyle={[styles.scrollContent, styles.compactScrollContent]}
          style={[styles.scroll, styles.compactScroll]}
          showsVerticalScrollIndicator
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
          scrollEnabled
        >
          <ImageBackground
            source={HoleIllustration}
            style={[styles.pageBackground, { minHeight: windowHeight }]}
            imageStyle={styles.pageBackgroundImage}
          >
            <View style={[styles.container, styles.containerCompact]}>
              {holeHeader}
              {debug ? (
                <>
                  <View style={styles.metaRow}>
                    {roundId ? <Text style={styles.meta}>{t('hole.roundId', { id: roundId })}</Text> : null}
                  </View>
                  {tourId ? <Text style={styles.meta}>{t('hole.tourId', { id: tourId })}</Text> : null}
                </>
              ) : null}
              {loading ? <Text style={styles.subtitle}>{t('course.loading')}</Text> : null}
              {error ? <Text style={styles.error}>{error}</Text> : null}
              {leaderboardPanel}
              {players.length === 0 && !loading ? <Text style={styles.subtitle}>{t('hole.noPlayers')}</Text> : null}
              <View style={styles.compactList}>
              {players.map((player) => (
                <View key={player.id} style={styles.compactRow}>
                  <View style={styles.compactInfoColumn}>
                    <View style={styles.compactNameRow}>
                      <View style={styles.compactNameBlock}>
                      {(() => {
                        const avatarUrl = player.imageThumbKey ?? player.imageKey ?? null;
                        return (
                          <View style={styles.compactNameLine}>
                            <View style={styles.compactAvatarWrap}>
                              {avatarUrl ? (
                                <Image source={{ uri: avatarUrl }} style={styles.compactAvatar} />
                              ) : (
                                <View style={styles.compactAvatarPlaceholder} />
                              )}
                            </View>
                            <Text style={styles.compactName}>{player.name}</Text>
                            {renderPlacementBadge(
                              roundPlacementByKey[normalizePlayerKey(player.id)] ??
                                roundPlacementByKey[normalizePlayerKey(player.name)],
                              tourPlacementByKey[normalizePlayerKey(player.id)] ??
                                tourPlacementByKey[normalizePlayerKey(player.name)]
                            )}
                          </View>
                        );
                      })()}
                      {strokeIndexForHole !== null
                        ? (() => {
                            const hcapStrokes =
                              (holeEntries.find((h) => h.playerId === player.id)?.handicapStrokes ??
                                computeStrokesReceivedForHole(player.handicap ?? null, strokeIndexForHole)) ?? 0;
                            const keyNorm = normalizePlayerKey(player.id);
                            const nameNorm = normalizePlayerKey(player.name);
                            const isTourLeader = tourLeaderKeys.has(keyNorm) || tourLeaderKeys.has(nameNorm);
                            const placement =
                              tourPlacementByKey[keyNorm] ??
                              tourPlacementByKey[nameNorm];
                            return (
                              <View style={styles.compactHandicapRow}>
                                <Text style={styles.compactHandicapText}>{`+${hcapStrokes}`}</Text>
                                {isTourLeader && !placement ? <Text style={styles.compactLeaderTrophy}> 🏆</Text> : null}
                              </View>
                            );
                          })()
                        : null}
                      </View>
                      {compactHazardColumns.length > 0 ? (
                        <View style={styles.compactHazardColumnRow}>
                          {(() => {
                            const hazardTypes = hazardIconsByPlayer.getTypesFor(player.id, player.name);
                            const eventKey = `${player.id}:${holeNumber}`;
                            const counts = eventCounts[eventKey] ?? { water: 0, penalty: 0, bunker: 0, threePutt: 0 };
                            return compactHazardColumns.map(({ type, icon }) => {
                              const count =
                                type === 'water'
                                  ? counts.water
                                  : type === 'penalty'
                                    ? counts.penalty
                                    : type === 'bunker'
                                      ? counts.bunker
                                      : counts.threePutt;
                              const field = type === 'threeputt' ? 'threePutt' : type;
                              return (
                                <View key={`${player.id}-hazard-${type}`} style={styles.compactHazardSlot}>
                                  {icon ? (
                                    <Pressable
                                      style={styles.compactHazardPressable}
                                      onPress={() => updateCompactHazardCount(player, field, 1)}
                                      onLongPress={() => updateCompactHazardCount(player, field, -1)}
                                      accessibilityLabel={t(`hole.event.${field}`)}
                                    >
                                      <Image
                                        source={icon}
                                        style={[
                                          styles.compactHazardIcon,
                                          !hazardTypes.has(type) && styles.compactHazardIconMuted,
                                        ]}
                                        resizeMode="contain"
                                      />
                                      <Text style={styles.compactHazardCount}>{count}</Text>
                                    </Pressable>
                                  ) : null}
                                </View>
                              );
                            });
                          })()}
                        </View>
                      ) : null}
                    </View>
                  </View>
                  {(() => {
                    const existing = holeEntries.find((entry) => {
                      if (entry.holeNumber !== holeNumber) return false;
                      const entryName = (entry.playerName ?? '').trim().toLowerCase();
                      const playerName = player.name.trim().toLowerCase();
                      return entry.playerId === player.id || entryName === playerName || entry.playerName === player.id;
                    });
                    const selectedFromExisting =
                      existing && existing.grossStrokes !== null ? String(existing.grossStrokes) : existing ? 'Pickup' : undefined;
                    const selectedScore =
                      scores[player.id] ??
                      scores[player.name] ??
                      selectedFromExisting;
                    const hasExplicitScore =
                      explicitScoreSelections[player.id] === true ||
                      explicitScoreSelections[player.name] === true;
                    const displayScore =
                      selectedScore === 'Pickup' && !hasExplicitScore ? undefined : selectedScore;
                    const stablefordPoints =
                      isStablefordGame && selectedScore && selectedScore !== 'Pickup' && parForHole
                        ? Math.max(
                            0,
                            2 +
                              parForHole -
                              computeNetForHole({
                                gross: selectedScore,
                                handicap: player.handicap ?? null,
                                holeIndex: strokeIndexForHole,
                                par: parForHole,
                              }).netStrokes
                          )
                        : null;
                    return (
                      <View style={styles.compactScoreActions}>
                        {mysteryWinnerIdsForHole.has(player.id) ? (
                          <Image source={MysteryPrizeIcon} style={styles.compactScoreMysteryIcon} resizeMode="contain" />
                        ) : null}
                        <Pressable
                          style={styles.compactScoreButton}
                          onPress={() => setScorePopupPlayerId(player.id)}
                        >
                          <Text
                            style={[
                              styles.compactScoreButtonText,
                              !displayScore && styles.compactScoreButtonTextPlaceholder,
                            ]}
                          >
                            {displayScore ?? t('hole.scoreButtonLabel')}
                          </Text>
                        </Pressable>
                        {isStablefordGame ? (
                          <Text style={styles.compactStablefordText}>
                            {stablefordPoints !== null ? String(stablefordPoints) : '–'}
                          </Text>
                        ) : null}
                      </View>
                    );
                  })()}
                </View>
              ))}
            </View>
            <Modal
              visible={!!scorePopupPlayerId}
              transparent
              animationType="fade"
              onRequestClose={() => setScorePopupPlayerId(null)}
            >
              <Pressable style={styles.modalBackdrop} onPress={() => setScorePopupPlayerId(null)}>
                <Pressable style={[styles.modalCard, styles.scoreModalCard]} onPress={() => null}>
                  <Text style={styles.modalTitle}>
                    {(() => {
                      const name = scorePopupPlayer?.name ?? '';
                      const hcapStrokes =
                        scorePopupPlayer && strokeIndexForHole !== null
                          ? (holeEntries.find((h) => h.playerId === scorePopupPlayer.id)?.handicapStrokes ??
                              computeStrokesReceivedForHole(scorePopupPlayer.handicap ?? null, strokeIndexForHole)) ?? 0
                          : 0;
                      const handicapSuffix = scorePopupPlayer ? ` +${hcapStrokes}` : '';
                      return t('hole.scorePopupTitle', {
                        name,
                        handicapSuffix,
                        holeLabel: t('hole.title'),
                        hole: holeNumber,
                      });
                    })()}
                  </Text>
                  <Text style={styles.modalSubtitle}>
                    {t('hole.scorePopupMeta', { hcpIndex: hcpLabel, par: parLabel })}
                  </Text>
                  <View style={styles.scorePopupGrid}>
                    {[...Array(9)].map((_, idx) => String(idx + 1)).concat('10+', 'Pickup').map((val) => {
                      const selected = scorePopupPlayer
                        ? scores[scorePopupPlayer.id] ?? scores[scorePopupPlayer.name]
                        : null;
                      const isParScore = parForHole ? val === String(parForHole) : false;
                      return (
                        <Pressable
                          key={val}
                          style={[styles.scorePopupButton, isParScore && styles.scorePopupButtonPar]}
                          onPress={() => {
                            if (!scorePopupPlayer) return;
                            handleScore(scorePopupPlayer, val);
                            if (mysteryEntriesForHole.length === 0) {
                              setScorePopupPlayerId(null);
                            }
                          }}
                          accessibilityRole="button"
                          accessibilityState={{ selected: selected === val }}
                        >
                          <Image
                            source={selected === val ? ScoreButtonSelectedImage : ScoreButtonImage}
                            style={styles.scorePopupButtonImage}
                            resizeMode="contain"
                            accessible={false}
                          />
                          <Text style={[styles.scorePopupButtonText, val === 'Pickup' && styles.scorePopupButtonTextSmall]}>
                            {val}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                  {scorePopupPlayer && mysteryEntriesForHole.length > 0 ? (
                    <View style={styles.modalSection}>
                      <Text style={styles.modalLabel}>{t('hole.mysteryPrizeWinnerTitle')}</Text>
                      {mysteryEntriesForHole.map((entry) => {
                        const selected = mysteryWinnersBySubgame[entry.subgameId]?.playerId === scorePopupPlayer.id;
                        return (
                          <Pressable
                            key={`score-popup-mystery-${entry.subgameId}`}
                            style={styles.modalRow}
                            onPress={() => handleMysteryWinnerSelect(entry.subgameId, scorePopupPlayer.id)}
                          >
                            <Text style={styles.modalLabel}>
                              {subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame')}
                            </Text>
                            <View style={styles.radio}>
                              {selected ? (
                                <>
                                  <View style={styles.radioSelected} />
                                  <Text style={styles.radioSelectedText}>✓</Text>
                                </>
                              ) : null}
                            </View>
                          </Pressable>
                        );
                      })}
                    </View>
                  ) : null}
                  <Pressable style={styles.modalCloseButton} onPress={() => setScorePopupPlayerId(null)}>
                    <Text style={styles.modalCloseText}>{t('hole.close')}</Text>
                  </Pressable>
                </Pressable>
              </Pressable>
            </Modal>
            <Modal
              visible={!!selectedPlayerId}
              transparent
              animationType="fade"
              onRequestClose={() => setSelectedPlayerId(null)}
            >
              <Pressable style={styles.modalBackdrop} onPress={() => setSelectedPlayerId(null)}>
                <Pressable style={styles.modalCard} onPress={() => null}>
                  <Text style={styles.modalTitle}>
                    {t('hole.playerBreakdownTitle', { name: selectedPlayer?.name ?? '' })}
                  </Text>
                  {selectedPlayerGamePoints.length > 0 ? (
                    selectedPlayerGamePoints.map((row) => (
                      <View key={row.id} style={styles.modalRow}>
                        <View style={styles.modalLabelGroup}>
                          {(() => {
                            const icon = GAME_ICON_MAP[normalizeGameSlug(row.name)];
                            return icon ? <Image source={icon} style={styles.modalGameIcon} resizeMode="contain" /> : null;
                          })()}
                          <Text style={styles.modalLabel}>
                            {row.name}
                            {(() => {
                              const pos = row.position ?? (selectedPlayer?.id ? gamePositionsByPlayer[row.id]?.[selectedPlayer.id] : null);
                              if (!pos) return '';
                              const isShared = !!positionShareByGame[row.id]?.[selectedPlayer?.id ?? ''];
                              const shareLabel = isShared ? `, ${t('hole.positionShared')}` : '';
                              return ` (${t('hole.positionShort', { position: pos })}${shareLabel})`;
                            })()}
                          </Text>
                        </View>
                        <Text style={styles.modalValue}>{row.points}</Text>
                      </View>
                    ))
                  ) : (
                    <Text style={styles.modalEmpty}>{t('hole.playerBreakdownEmpty')}</Text>
                  )}
                  {!isCompactMode ? (
                    <View style={styles.roundStateBlock}>
                      <Text style={styles.roundStateTitle}>{t('hole.roundStateTitle')}</Text>
                      <ScrollView style={styles.roundStateScroll} contentContainerStyle={styles.roundStateScrollContent}>
                        <Text style={styles.roundStateText}>
                          {roundStateMessage ?? t('hole.roundStateEmpty')}
                        </Text>
                      </ScrollView>
                    </View>
                  ) : null}
                  <Pressable style={styles.modalCloseButton} onPress={() => setSelectedPlayerId(null)}>
                    <Text style={styles.modalCloseText}>{t('hole.close')}</Text>
                  </Pressable>
                </Pressable>
              </Pressable>
            </Modal>
            <Modal
              visible={nextMysteryModalOpen}
              transparent
              animationType="fade"
              onRequestClose={() => setNextMysteryModalOpen(false)}
            >
              <Pressable style={styles.modalBackdrop} onPress={() => setNextMysteryModalOpen(false)}>
                <Pressable style={styles.modalCard} onPress={() => null}>
                  <Text style={styles.modalTitle}>{t('hole.mysteryPrizeNextHoleTitle')}</Text>
                  <Text style={styles.modalSubtitle}>{t('hole.mysteryPrizeNextHoleSubtitle', { hole: holeNumber + 1 })}</Text>
                  {nextMysteryModalRows.map((row, index) => (
                    <View key={`${row.subgame}:${row.player}:${index}`} style={styles.modalRow}>
                      <Text style={styles.modalLabel}>{row.subgame}</Text>
                      <Text style={styles.modalValue}>{row.player}</Text>
                    </View>
                  ))}
                  <Pressable style={styles.modalCloseButton} onPress={() => setNextMysteryModalOpen(false)}>
                    <Text style={styles.modalCloseText}>{t('hole.close')}</Text>
                  </Pressable>
                </Pressable>
              </Pressable>
            </Modal>
            <View style={styles.navRowBottom}>
              <Pressable
                style={[styles.navButton, holeNumber <= 1 && styles.navButtonDisabled]}
                onPress={() =>
                  holeNumber > 1 && navigateToHole(holeNumber - 1, { swipe: false, compact: true })
                }
                disabled={holeNumber <= 1}
              >
                <Text style={[styles.navText, holeNumber <= 1 && styles.navTextDisabled]}>
                  {t('hole.prevHole')}
                </Text>
              </Pressable>
              {isLastHole ? (
                <Pressable style={[styles.navButton, styles.navButtonNext]} onPress={finishRound}>
                  <Text style={[styles.navText, styles.navTextOnGreen]}>{t('hole.finishRound')}</Text>
                </Pressable>
              ) : (
                <Pressable
                  style={[styles.navButton, styles.navButtonNext]}
                  onPress={() => navigateToHole(holeNumber + 1, { swipe: false, compact: true })}
                >
                  <Text style={[styles.navText, styles.navTextOnGreen]}>{t('hole.nextHole')}</Text>
                </Pressable>
              )}
            </View>
          {gameResultsPanel ? (
              <View style={styles.compactGameResultsUnderNav}>
                {gameResultsPanel}
              </View>
            ) : null}
          </View>
        </ImageBackground>
      </ScrollView>
      {celebrating ? (
        <View style={[styles.celebrateOverlay, { pointerEvents: 'box-none' }]}>
          {!showFinaleOnly ? (
            <Animated.Text style={[styles.bottle, { transform: [{ scale: bottleScale }] }]}>🍾</Animated.Text>
          ) : null}
          {showFinaleOnly ? (
            <Animated.Text style={[styles.finale, { transform: [{ scale: finaleScale }] }]}>🎆</Animated.Text>
          ) : null}
        </View>
      ) : null}
      {birdsVisible ? (
        <View style={[styles.birdieOverlay, { pointerEvents: 'none' }]}>
          <Animated.Image
            source={BirdieImage}
            style={[
              styles.birdieImage,
              {
                opacity: birdieOpacity,
                transform: [
                  { scale: birdieScale },
                  {
                    rotate: birdieRotate.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['0deg', '360deg'],
                    }),
                  },
                ],
              },
            ]}
            resizeMode="contain"
          />
        </View>
      ) : null}
      </View>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={styles.scrollContent}
      style={styles.scroll}
      showsVerticalScrollIndicator
    >
      <ImageBackground source={HoleIllustration} style={styles.pageBackground} imageStyle={styles.pageBackgroundImage}>
        <View style={[styles.container, isSwipeMode && styles.containerSwipe]}>
      {!isSwipeMode ? holeHeader : null}
      {!isSwipeMode && debug ? (
        <>
          <View style={styles.metaRow}>
            {roundId ? <Text style={styles.meta}>{t('hole.roundId', { id: roundId })}</Text> : null}
          </View>
          {tourId ? <Text style={styles.meta}>{t('hole.tourId', { id: tourId })}</Text> : null}
        </>
      ) : null}
      {!isSwipeMode && loading ? <Text style={styles.subtitle}>{t('course.loading')}</Text> : null}
      {!isSwipeMode && error ? <Text style={styles.error}>{error}</Text> : null}
      {!isSwipeMode ? topPanels : null}

      {!isSwipeMode && players.length === 0 && !loading ? (
        <Text style={styles.subtitle}>{t('hole.noPlayers')}</Text>
      ) : null}

      {players.map(renderPlayerCard)}
      <Modal
        visible={!!selectedPlayerId}
        transparent
        animationType="fade"
        onRequestClose={() => setSelectedPlayerId(null)}
      >
        <Pressable style={styles.modalBackdrop} onPress={() => setSelectedPlayerId(null)}>
          <Pressable style={styles.modalCard} onPress={() => null}>
            <Text style={styles.modalTitle}>
              {t('hole.playerBreakdownTitle', { name: selectedPlayer?.name ?? '' })}
            </Text>
            {selectedPlayerGamePoints.length > 0 ? (
              selectedPlayerGamePoints.map((row) => (
                <View key={row.id} style={styles.modalRow}>
                  <View style={styles.modalLabelGroup}>
                    {(() => {
                      const icon = GAME_ICON_MAP[normalizeGameSlug(row.name)];
                      return icon ? <Image source={icon} style={styles.modalGameIcon} resizeMode="contain" /> : null;
                    })()}
                    <Text style={styles.modalLabel}>
                      {row.name}
                      {(() => {
                        const pos = row.position ?? (selectedPlayer?.id ? gamePositionsByPlayer[row.id]?.[selectedPlayer.id] : null);
                        if (!pos) return '';
                        const isShared = !!positionShareByGame[row.id]?.[selectedPlayer?.id ?? ''];
                        const shareLabel = isShared ? `, ${t('hole.positionShared')}` : '';
                        return ` (${t('hole.positionShort', { position: pos })}${shareLabel})`;
                      })()}
                    </Text>
                  </View>
                  <Text style={styles.modalValue}>{row.points}</Text>
                </View>
              ))
            ) : (
              <Text style={styles.modalEmpty}>{t('hole.playerBreakdownEmpty')}</Text>
            )}
            <View style={styles.roundStateBlock}>
              <Text style={styles.roundStateTitle}>{t('hole.roundStateTitle')}</Text>
              <ScrollView style={styles.roundStateScroll} contentContainerStyle={styles.roundStateScrollContent}>
                <Text style={styles.roundStateText}>
                  {roundStateMessage ?? t('hole.roundStateEmpty')}
                </Text>
              </ScrollView>
            </View>
            <Pressable style={styles.modalCloseButton} onPress={() => setSelectedPlayerId(null)}>
              <Text style={styles.modalCloseText}>{t('hole.close')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal
        visible={mysteryModalOpen}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setMysteryModalOpen(false);
        }}
      >
        <Pressable
          style={styles.modalBackdrop}
          onPress={() => {
            setMysteryModalOpen(false);
          }}
        >
          <Pressable style={styles.modalCard} onPress={() => null}>
            <Text style={styles.modalTitle}>{t('hole.mysteryPrizeWinnerTitle')}</Text>
            {mysteryEntriesForHole.map((entry) => (
              <View key={entry.subgameId} style={styles.modalSection}>
                <Text style={styles.modalLabel}>
                  {subgameNameById[entry.subgameId] ?? t('hole.mysteryPrizeUnknownSubgame')}
                </Text>
                {players.map((player) => {
                  const selected = mysteryWinnersBySubgame[entry.subgameId]?.playerId === player.id;
                  return (
                    <Pressable
                      key={player.id}
                      style={styles.modalRow}
                      onPress={() => handleMysteryWinnerSelect(entry.subgameId, player.id)}
                    >
                      <Text style={styles.modalLabel}>{player.name}</Text>
                      <View style={styles.radio}>
                        {selected ? (
                          <>
                            <View style={styles.radioSelected} />
                            <Text style={styles.radioSelectedText}>✓</Text>
                          </>
                        ) : null}
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            ))}
            {mysteryEntriesForHole.length === 0 ? (
              <Text style={styles.modalEmpty}>{t('hole.mysteryPrizeNone')}</Text>
            ) : null}
            <Pressable
              style={styles.modalCloseButton}
              onPress={() => {
                setMysteryModalOpen(false);
              }}
            >
              <Text style={styles.modalCloseText}>{t('hole.close')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
      <Modal
        visible={nextMysteryModalOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setNextMysteryModalOpen(false)}
      >
        <Pressable style={styles.modalBackdrop} onPress={() => setNextMysteryModalOpen(false)}>
          <Pressable style={styles.modalCard} onPress={() => null}>
            <Text style={styles.modalTitle}>{t('hole.mysteryPrizeNextHoleTitle')}</Text>
            <Text style={styles.modalSubtitle}>{t('hole.mysteryPrizeNextHoleSubtitle', { hole: holeNumber + 1 })}</Text>
            {nextMysteryModalRows.map((row, index) => (
              <View key={`${row.subgame}:${row.player}:${index}`} style={styles.modalRow}>
                <Text style={styles.modalLabel}>{row.subgame}</Text>
                <Text style={styles.modalValue}>{row.player}</Text>
              </View>
            ))}
            <Pressable style={styles.modalCloseButton} onPress={() => setNextMysteryModalOpen(false)}>
              <Text style={styles.modalCloseText}>{t('hole.close')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
      {!isSwipeMode ? (
        <View style={styles.navRowBottom}>
          <Pressable
            style={[styles.navButton, holeNumber <= 1 && styles.navButtonDisabled]}
            onPress={() =>
              holeNumber > 1 && navigateToHole(holeNumber - 1, { swipe: false })
            }
            disabled={holeNumber <= 1}
          >
            <Text style={[styles.navText, holeNumber <= 1 && styles.navTextDisabled]}>←</Text>
          </Pressable>
          {isLastHole ? (
            <Pressable style={[styles.navButton, styles.navButtonNext]} onPress={finishRound}>
              <Text style={[styles.navText, styles.navTextOnGreen]}>{t('hole.finishRound')}</Text>
            </Pressable>
          ) : (
            <Pressable
              style={[styles.navButton, styles.navButtonNext]}
              onPress={() => navigateToHole(holeNumber + 1, { swipe: false })}
            >
              <Text style={[styles.navText, styles.navTextOnGreen]}>→</Text>
            </Pressable>
          )}
        </View>
      ) : null}
      {celebrating ? (
        <View style={[styles.celebrateOverlay, { pointerEvents: 'box-none' }]}>
          {!showFinaleOnly ? <Animated.Text style={[styles.bottle, { transform: [{ scale: bottleScale }] }]}>🍾</Animated.Text> : null}
          {showFinaleOnly ? <Animated.Text style={[styles.finale, { transform: [{ scale: finaleScale }] }]}>🎆</Animated.Text> : null}
        </View>
      ) : null}
      {birdsVisible ? (
        <View style={[styles.birdieOverlay, { pointerEvents: 'none' }]}>
          <Animated.Image
            source={BirdieImage}
            style={[
              styles.birdieImage,
              {
                opacity: birdieOpacity,
                transform: [
                  { scale: birdieScale },
                  {
                    rotate: birdieRotate.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['0deg', '360deg'],
                    }),
                  },
                ],
              },
            ]}
            resizeMode="contain"
          />
        </View>
      ) : null}
        </View>
      </ImageBackground>
    </ScrollView>
  );
}

function generateTraceId() {
  const uuid = (crypto as { randomUUID?: () => string }).randomUUID?.();
  if (uuid) return uuid;
  return `trace-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 3px 8px rgba(0,0,0,0.25)' },
  default: {
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
  },
});

const blockShadow = Platform.select({
  web: { boxShadow: '0px 2px 6px rgba(0,0,0,0.2)' },
  default: {
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
  },
});
const swipeSnap = Platform.select({
  web: { scrollSnapType: 'y mandatory' },
  default: {},
});
const swipeSnapItem = Platform.select({
  web: { scrollSnapAlign: 'start', scrollSnapStop: 'always' },
  default: {},
});

const styles = StyleSheet.create({
  scroll: {
    backgroundColor: '#0c1328',
  },
  compactScroll: {
    flex: 1,
  },
  compactScrollContent: {
    flexGrow: 1,
    paddingBottom: 180,
  },
  swipeRoot: {
    flex: 1,
    backgroundColor: '#0c1328',
    ...Platform.select({
      web: { height: '100vh' },
      default: {},
    }),
  },
  swipeList: {
    flex: 1,
    ...swipeSnap,
    ...Platform.select({
      web: { height: '100vh' },
      default: {},
    }),
  },
  scrollContent: {
    flexGrow: 1,
  },
  swipeContent: {
    flexGrow: 1,
  },
  compactList: {
    gap: 10,
    marginTop: 8,
    backgroundColor: 'transparent',
  },
  compactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: '#0f1c3c',
    borderWidth: 1,
    borderColor: '#24346b',
  },
  compactInfoColumn: {
    flex: 1,
    marginRight: 10,
    backgroundColor: 'transparent',
  },
  compactNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    backgroundColor: 'transparent',
  },
  compactNameLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    flexShrink: 1,
    backgroundColor: 'transparent',
  },
  compactNameBlock: {
    width: 200,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  compactAvatarWrap: {
    width: 28,
    height: 28,
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: 0,
    borderColor: 'transparent',
    backgroundColor: 'transparent',
  },
  compactAvatar: {
    width: '100%',
    height: '100%',
  },
  compactAvatarPlaceholder: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  compactName: {
    color: '#e5edff',
    fontSize: 16,
    fontWeight: '600',
    flexShrink: 1,
  },
  roundPlacementBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    backgroundColor: 'transparent',
  },
  roundPlacementMedal: {
    fontSize: 13,
  },
  roundPlacementCupIcon: {
    width: 14,
    height: 14,
  },
  roundPlacementCupIconSilver: {
    tintColor: '#c0c6d4',
  },
  roundPlacementCupIconBronze: {
    tintColor: '#cd7f32',
  },
  compactLeaderStar: {
    color: '#facc15',
  },
  compactLeaderTrophy: {
    color: '#facc15',
  },
  compactHandicapRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  compactHandicapText: {
    color: '#9fb2dd',
    fontSize: 14,
    fontWeight: '600',
  },
  compactEventRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'transparent',
  },
  compactEventItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'transparent',
  },
  compactEventIcon: {
    width: 35,
    height: 35,
  },
  compactEventCount: {
    color: '#dbe7ff',
    fontSize: 12,
    fontWeight: '600',
  },
  compactHazardIcon: {
    width: 25,
    height: 25,
  },
  compactHazardIconMuted: {
    opacity: 0.8,
  },
  compactHazardColumnRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  compactHazardSlot: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    width: 44,
    backgroundColor: 'transparent',
  },
  compactHazardPressable: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  compactHazardCount: {
    color: '#dbe7ff',
    fontSize: 11,
    fontWeight: '700',
  },
  compactScoreButton: {
    minWidth: 72,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: '#16a34a',
    borderWidth: 2,
    borderColor: '#16a34a',
    alignItems: 'center',
    shadowColor: '#0b1228',
    shadowOpacity: 0.9,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 10 },
    elevation: 16,
  },
  compactScoreActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'transparent',
  },
  compactScoreMysteryIcon: {
    width: 20,
    height: 20,
  },
  compactScoreButtonText: {
    color: '#f8fafc',
    fontWeight: '800',
  },
  compactScoreButtonTextPlaceholder: {
    color: '#ffffff',
  },
  compactStablefordText: {
    color: '#93c5fd',
    fontWeight: '700',
    minWidth: 16,
    textAlign: 'center',
  },
  scorePopupGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 12,
  },
  scorePopupButton: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 90,
    height: 60,
  },
  scorePopupButtonPar: {
    borderWidth: 2,
    borderColor: '#f59e0b',
    borderRadius: 8,
  },
  scorePopupButtonImage: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  scorePopupButtonText: {
    color: '#000000',
    marginTop: -6,
    fontSize: 24,
    fontWeight: '700',
  },
  scorePopupButtonTextSmall: {
    fontSize: 16,
  },
  pageBackground: {
    flex: 1,
    width: '100%',
    backgroundColor: 'transparent',
    position: 'relative',
  },
  pageBackgroundImage: {
    opacity: 0.28,
  },
  container: {
    padding: 12,
    gap: 8,
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  containerCompact: {
    justifyContent: 'flex-start',
  },
  containerSwipe: {
    justifyContent: 'flex-start',
  },
  compactRoot: {
    flex: 1,
    position: 'relative',
  },
  playerPage: {
    padding: 12,
    gap: 12,
    justifyContent: 'flex-start',
    position: 'relative',
    backgroundColor: 'transparent',
    ...swipeSnapItem,
  },
  playerPageLeaderBackground: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.2,
    width: '100%',
    height: '100%',
    zIndex: 0,
    pointerEvents: 'none',
  },
  playerPageContent: {
    flex: 1,
    zIndex: 1,
    backgroundColor: 'transparent',
  },
  pageSpacer: {
    flexGrow: 1,
    backgroundColor: 'transparent',
  },
  holeNavRowSwipe: {
    flexDirection: 'row',
    gap: 12,
    paddingBottom: 12,
  },
  holeNavButtonSwipe: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 16,
    backgroundColor: '#1f2d4f',
    borderWidth: 1,
    borderColor: '#2c3a6a',
    alignItems: 'center',
  },
  holeNavButtonSwipeNext: {
    backgroundColor: '#16a34a',
    borderColor: '#16a34a',
  },
  holeNavButtonDisabled: {
    backgroundColor: '#0f172a',
    borderColor: '#1f2a44',
  },
  holeNavTextSwipe: {
    color: '#e5edff',
    fontSize: 14,
    fontWeight: '700',
  },
  holeNavTextOnGreen: {
    color: '#ffffff',
  },
  holeNavTextDisabled: {
    color: '#64748b',
  },
  holeHeaderSwipe: {
    position: 'relative',
    alignItems: 'center',
    gap: 6,
    paddingTop: 10,
    paddingBottom: 6,
    backgroundColor: 'transparent',
  },
  holeHeaderSwipeLeader: {
    overflow: 'hidden',
  },
  holeHeaderSwipeTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  holeHeaderSwipeTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#f8fafc',
  },
  leaderStar: {
    fontSize: 18,
    color: '#fbbf24',
  },
  holeHeaderSwipeMeta: {
    fontSize: 13,
    color: '#c7d2fe',
  },
  leaderboardSwipe: {
    marginTop: 6,
    marginBottom: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 12,
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    gap: 6,
  },
  nextHoleCard: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
    backgroundColor: 'rgba(18, 26, 40, 0.82)',
    padding: 18,
    alignItems: 'center',
    gap: 8,
  },
  nextHoleTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#f8fafc',
  },
  nextHoleHint: {
    fontSize: 14,
    color: 'rgba(248,250,252,0.7)',
    textAlign: 'center',
  },
  nextHoleButton: {
    marginTop: 10,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 16,
    backgroundColor: 'rgba(252, 211, 77, 0.9)',
  },
  nextHoleButtonNext: {
    backgroundColor: '#16a34a',
  },
  nextHoleButtonDisabled: {
    backgroundColor: 'rgba(148, 163, 184, 0.35)',
  },
  nextHoleButtonText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1f2937',
  },
  nextHoleButtonTextOnGreen: {
    color: '#ffffff',
  },
  nextHoleButtonTextDisabled: {
    color: '#94a3b8',
  },
  gameMatrixHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'transparent',
  },
  gameMatrixCorner: {
    width: 90,
    backgroundColor: 'transparent',
  },
  gameMatrixPlayer: {
    flex: 1,
    color: '#e2e8ff',
    fontWeight: '700',
    fontSize: 12,
    textAlign: 'center',
    backgroundColor: 'transparent',
  },
  gameMatrixRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    backgroundColor: 'transparent',
  },
  gameMatrixGame: {
    width: 90,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  gameColumnIcon: {
    width: 20,
    height: 20,
    borderRadius: 4,
  },
  gameColumnTitle: {
    flex: 1,
    color: '#e2e8ff',
    fontWeight: '700',
    fontSize: 12,
  },
  gameMatrixScore: {
    flex: 1,
    textAlign: 'center',
    color: '#34d399',
    fontWeight: '700',
    fontSize: 12,
    backgroundColor: 'transparent',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    justifyContent: 'space-between',
    backgroundColor: 'transparent',
  },
  holeTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    backgroundColor: 'transparent',
  },
  holeTitle: {
    flex: 1,
    fontSize: 22,
    fontWeight: '800',
    color: '#f8fafc',
    backgroundColor: 'transparent',
  },
  mysteryPrizeIcon: {
    width: 23,
    height: 23,
  },
  mysteryWinnerIcon: {
    width: 16,
    height: 16,
  },
  mysteryPrizeText: {
    color: '#e8f0ff',
    fontSize: 13,
    textAlign: 'center',
  },
  mysteryPrizeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  meta: {
    textAlign: 'center',
    color: '#9fb3ff',
  },
  holeInfoBlock: {
    alignItems: 'center',
    gap: 4,
    padding: 10,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: 'transparent',
  },
  holeInfoImage: {
    opacity: 0.18,
  },
  holeInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  holeInfoBadge: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#2f4378',
    backgroundColor: 'transparent',
    color: '#e8f0ff',
    fontWeight: '700',
  },
  holeInfoName: {
    color: '#e5edff',
    fontWeight: '700',
    textAlign: 'center',
  },
  holeInfoDescription: {
    color: '#c7d3ff',
    textAlign: 'center',
    lineHeight: 18,
  },
  detailsToggle: {
    marginTop: 4,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#2f4378',
    backgroundColor: 'rgba(8, 12, 25, 0.5)',
  },
  detailsToggleText: {
    color: '#9fb3ff',
    fontWeight: '600',
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
  },
  subtitle: {
    textAlign: 'center',
    fontSize: 16,
    color: '#d5e1ff',
  },
  error: {
    textAlign: 'center',
    color: '#ff7b7b',
  },
  playerCard: {
    padding: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#24346b',
    backgroundColor: 'rgba(11, 18, 36, 0.7)',
    gap: 2,
    ...cardShadow,
  },
  playerCardLeader: {
    backgroundColor: 'transparent',
  },
  playerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    justifyContent: 'space-between',
  },
  eventRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    position: 'relative',
    backgroundColor: 'transparent',
  },
  eventItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  eventItemSwipe: {
    gap: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 14,
  },
  eventIcon: {
    fontSize: 21,
  },
  eventImage: {
    width: 22,
    height: 22,
  },
  eventImageSwipe: {
    width: 34,
    height: 34,
  },
  eventCount: {
    fontSize: 12,
    fontWeight: '700',
    color: '#e5edff',
  },
  eventCountSwipe: {
    fontSize: 16,
  },
  eventBubble: {
    position: 'absolute',
    bottom: 28,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  eventBubbleText: {
    fontSize: 12,
    color: '#e5edff',
    fontWeight: '700',
  },
  playerName: {
    fontSize: 16,
    fontWeight: '700',
    color: '#e5edff',
  },
  playerNameSwipe: {
    fontSize: 24,
  },
  playerNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  selectedScore: {
    color: '#1d4ed8',
    fontWeight: '600',
  },
  netHintGain: {
    color: '#22c55e',
  },
  netHintLoss: {
    color: '#f59e0b',
  },
  netHintEven: {
    color: '#1d4ed8',
  },
  netSummary: {
    fontWeight: '700',
    fontSize: 13,
    color: '#e5edff',
  },
  stablefordSummary: {
    marginTop: 2,
    fontSize: 13,
    fontWeight: '700',
    color: '#34d399',
  },
  scoreRowContainer: {
    marginTop: 0,
    backgroundColor: 'transparent',
  },
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 2,
  },
  scoreRowSwipe: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    rowGap: 0,
    columnGap: 0,
    paddingVertical: 0,
    backgroundColor: 'transparent',
  },
  scoreButton: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#1aa374',
    minWidth: 34,
    minHeight: 34,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#34d399',
    position: 'relative',
  },
  scoreButtonSwipe: {
    width: '22%',
    minHeight: 45,
    borderWidth: 0,
    backgroundColor: 'transparent',
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  scoreButtonSelected: {
    backgroundColor: '#1d4ed8',
    borderColor: '#1e40af',
  },
  scoreButtonParBorder: {
    borderWidth: 2,
    borderColor: '#ffffff',
  },
  scoreButtonImage: {
    width: 44,
    height: 64,
  },
  scoreButtonImageSwipe: {
    width: 80,
    height: 50,
  },
  scoreButtonLabel: {
    position: 'absolute',
    color: '#0c1328',
    fontWeight: '800',
    fontSize: 13,
    top: 12,
  },
  scoreButtonLabelSwipe: {
    fontSize: 18,
  },
  scoreText: {
    color: '#0c1328',
    fontWeight: '800',
    fontSize: 13,
  },
  scoreTextSelected: {
    color: '#e5edff',
  },
  puttRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 12,
  },
  puttButton: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderWidth: 0,
    paddingVertical: 0,
    paddingHorizontal: 0,
    position: 'relative',
  },
  puttButtonImage: {
    width: '110%',
    height: 56,
  },
  puttButtonContent: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  puttLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  puttText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0c1328',
    marginTop: 11,
  },
  puttBallRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    marginRight: 14,
    marginTop: 13,
  },
  puttBallIcon: {
    width: 30,
    height: 30,
    marginHorizontal: -10,
    backgroundColor: 'transparent',
  },
  netDot: {
    position: 'absolute',
    top: 1,
    right: 1,
    width: 8,
    height: 8,
    borderRadius: 5,
    backgroundColor: '#ffffff',
  },
  pickupButton: {
    borderColor: '#f59e0b',
    backgroundColor: '#fbbf24',
  },
  pickupText: {
    color: '#0c1328',
    fontWeight: '800',
    fontSize: 13,
  },
  scoreboard: {
    padding: 8,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#24346b',
    backgroundColor: 'rgba(15, 28, 60, 0.7)',
    gap: 8,
    flexDirection: 'column',
    ...blockShadow,
  },
  gameResultsFrame: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1f2d4f',
    backgroundColor: 'rgba(10, 16, 32, 0.5)',
    padding: 6,
  },
  scoreboardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#e5edff',
  },
  scoreboardEmpty: {
    color: '#9fb4d9',
    fontSize: 13,
  },
  scoreboardRow: {
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
    backgroundColor: 'transparent',
  },
  scoreboardRowLeader: {
    overflow: 'hidden',
  },
  scoreboardRowContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    zIndex: 1,
  },
  scoreboardLeaderBackground: {
    position: 'absolute',
    right: 8,
    top: -4,
    width: 64,
    height: 48,
    opacity: 0.18,
  },
  leaderNameIcon: {
    width: 20,
    height: 20,
    marginTop: 4,
  },
  scoreboardNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    backgroundColor: 'transparent',
  },
  scoreboardName: {
    fontWeight: '600',
    color: '#d5e1ff',
  },
  scoreboardValue: {
    fontWeight: '700',
    color: '#34d399',
    backgroundColor: 'transparent',
  },
  scoreboardValueDetails: {
    textDecorationLine: 'underline',
  },
  gameResultsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    paddingVertical: 2,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(8, 10, 20, 0.72)',
    justifyContent: 'center',
    padding: 14,
  },
  modalCard: {
    backgroundColor: '#0f1c3c',
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: '#24346b',
    gap: 12,
  },
  scoreModalCard: {
    paddingHorizontal: 10,
  },
  modalTitle: {
    color: '#f8fafc',
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
  },
  modalSubtitle: {
    color: '#c7d2fe',
    fontSize: 13,
    textAlign: 'center',
  },
  roundStateBlock: {
    gap: 6,
  },
  roundStateTitle: {
    color: '#dbeafe',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  roundStateScroll: {
    maxHeight: 180,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(8, 12, 25, 0.5)',
  },
  roundStateScrollContent: {
    padding: 10,
  },
  roundStateText: {
    color: '#c7d2fe',
    fontSize: 11,
  },
  infoIconSmall: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: '#64748b',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoIconSmallText: {
    color: '#94a3b8',
    fontSize: 10,
    fontWeight: '700',
  },
  modalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#1f2d4f',
    gap: 10,
  },
  modalSection: {
    gap: 6,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#1f2d4f',
  },
  radio: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#8aa0df',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  radioSelected: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#34d399',
    position: 'absolute',
  },
  radioSelectedText: {
    color: '#0f1c3c',
    fontSize: 10,
    fontWeight: '800',
    position: 'absolute',
  },
  modalLabel: {
    color: '#d5e1ff',
  },
  modalLabelGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  modalGameIcon: {
    width: 40,
    height: 40,
    borderRadius: 4,
  },
  modalValue: {
    color: '#34d399',
    fontWeight: '700',
  },
  modalEmpty: {
    color: '#9fb3ff',
  },
  modalCloseButton: {
    marginTop: 8,
    alignSelf: 'flex-end',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1aa374',
  },
  modalBackButton: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#4b5d97',
    backgroundColor: '#0f1c3c',
  },
  modalBackText: {
    color: '#cbd8ff',
    fontSize: 12,
    fontWeight: '700',
  },
  modalCloseText: {
    color: '#34d399',
    fontWeight: '700',
  },
  gameResultsHeader: {
    borderBottomWidth: 1,
    borderBottomColor: '#24346b',
    paddingBottom: 2,
    marginBottom: 2,
  },
  gameResultsName: {
    flex: 1.8,
    fontWeight: '700',
    fontSize: 12,
    color: '#e5edff',
  },
  gameResultsPlayer: {
    flex: 1,
    textAlign: 'center',
    color: '#d5e1ff',
    fontWeight: '600',
    fontSize: 12,
    backgroundColor: 'transparent',
  },
  gameResultCell: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 2,
    paddingHorizontal: 4,
  },
  gameResultValueWrapper: {
    paddingVertical: 2,
    paddingHorizontal: 6,
  },
  gameResultValue: {
    textAlign: 'center',
    fontWeight: '800',
    color: '#34d399',
  },
  gameResultLeader: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: '#ffffff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gameResultLeaderShared: {
    borderColor: '#64748b',
  },
  scoreboardNameContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  hazardIcon: {
    width: 16,
    height: 16,
  },
  gameResultLeaderText: {
    color: '#ffffff',
  },
  navRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
  },
  navRowBottom: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 12,
    backgroundColor: 'transparent',
  },
  compactGameResultsUnderNav: {
    marginTop: 12,
    backgroundColor: 'transparent',
  },
  navButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1aa374',
    alignItems: 'center',
    backgroundColor: '#34d399',
  },
  navButtonNext: {
    borderColor: '#16a34a',
    backgroundColor: '#16a34a',
  },
  navButtonDisabled: {
    borderColor: '#2c3a6a',
    backgroundColor: '#0a0f1e',
  },
  navText: {
    color: '#0c1328',
    fontWeight: '800',
  },
  navTextOnGreen: {
    color: '#ffffff',
  },
  navTextDisabled: {
    color: '#6b7280',
  },
  celebrateOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    zIndex: 10,
  },
  bottle: {
    fontSize: 48,
    color: '#fff',
  },
  birdieOverlay: {
    ...StyleSheet.absoluteFillObject,
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    backgroundColor: 'transparent',
    zIndex: 9999,
    elevation: 9999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  birdieImage: {
    width: 100,
    height: 100,
  },
  fireworks: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  firework: {
    position: 'absolute',
    fontSize: 40,
  },
});
